<template>
  <div class="history-owe-fee-import">
    <el-dialog
      :visible.sync="dialogVisible"
      title="历史欠费兼容导入（测试）"
      width="96%"
      top="2vh"
      custom-class="history-owe-fee-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="!submitting"
      :before-close="handleBeforeClose"
      @closed="reset"
    >
      <el-steps :active="activeStep" simple finish-status="success" class="import-steps">
        <el-step title="上传表格" icon="el-icon-upload2" />
        <el-step title="匹配字段" icon="el-icon-connection" />
        <el-step title="匹配房屋" icon="el-icon-office-building" />
        <el-step title="计费规则" icon="el-icon-setting" />
        <el-step title="核对确认" icon="el-icon-document-checked" />
      </el-steps>

      <div v-if="importTargetType && activeStep > 0" class="import-target-indicator">
        <span>本次仅导入</span>
        <el-tag :type="importTargetType === 'SHOP' ? 'warning' : 'success'" effect="plain">
          {{ importTargetLabel }}
        </el-tag>
        <span>房屋档案与欠费明细均已按该类型隔离</span>
      </div>

      <section v-if="activeStep === 0" class="step-section upload-section">
        <div class="import-target-selector">
          <strong>选择本次导入类型</strong>
          <el-radio-group :value="importTargetType" size="medium" @input="handleImportTargetChange">
            <el-radio-button
              v-for="option in importTargetOptions"
              :key="option.value"
              :label="option.value"
            >
              {{ option.label }}
            </el-radio-button>
          </el-radio-group>
          <span>{{ importTargetDescription }}</span>
        </div>
        <el-upload
          ref="upload"
          drag
          action=""
          accept=".xls,.xlsx"
          :auto-upload="false"
          :show-file-list="false"
          :on-change="handleFileChange"
          :disabled="parsing || !importTargetType"
        >
          <i class="el-icon-upload" />
          <div class="el-upload__text">将不同平台导出的 Excel 拖到这里，或<em>选择文件</em></div>
          <div slot="tip" class="el-upload__tip">支持 .xls、.xlsx，单个文件不超过 10 MB，最多读取 {{ sourceRowLimit }} 条源数据</div>
        </el-upload>
        <div v-if="!importTargetType" class="target-required-tip">请先选择普通住宅或商铺</div>
        <div class="upload-notes">
          <span><i class="el-icon-search" /> 自动识别前 50 行中的字段标题</span>
          <span><i class="el-icon-connection" /> 不同平台列名可在下一步人工匹配</span>
          <span><i class="el-icon-view" /> 文件仅在当前浏览器解析，确认后才提交欠费明细</span>
        </div>
        <div v-if="parsing" class="parsing-tip"><i class="el-icon-loading" /> 正在解析表格...</div>
      </section>

      <section v-else-if="activeStep === 1" class="step-section">
        <div class="source-overview">
          <div>
            <strong>{{ sourceFileName }}</strong>
            <span>{{ sourceRows.length }} 条数据 · {{ sourceHeaders.length }} 列 · 表头第 {{ detectedHeaderRow }} 行</span>
          </div>
          <div class="source-actions">
            <el-select v-if="sheetNames.length > 1" v-model="selectedSheetName" size="small" :disabled="!workbook" @change="handleSheetChange">
              <el-option v-for="name in sheetNames" :key="name" :label="name" :value="name" />
            </el-select>
            <el-button size="small" icon="el-icon-magic-stick" @click="autoMatchMappings(true)">重新自动匹配</el-button>
          </div>
        </div>
        <el-alert
          v-if="mappingErrors.length"
          type="warning"
          :closable="false"
          show-icon
          :title="mappingErrors.join('；')"
          class="step-alert"
        />
        <el-table :data="fieldDefinitions" border stripe max-height="510" class="mapping-table">
          <el-table-column label="系统标准字段" min-width="190" fixed="left">
            <template slot-scope="scope">
              <div class="field-name">
                <strong>{{ scope.row.label }}</strong>
                <el-tag v-if="scope.row.required" size="mini" type="danger" effect="plain">必要</el-tag>
                <el-tag v-else size="mini" type="info" effect="plain">辅助</el-tag>
              </div>
              <div class="field-description">{{ scope.row.description }}</div>
            </template>
          </el-table-column>
          <el-table-column label="上传表格列" min-width="260">
            <template slot-scope="scope">
              <el-select
                :value="mappings[scope.row.key]"
                clearable
                filterable
                placeholder="不匹配该字段"
                style="width:100%"
                @change="setFieldMapping(scope.row.key, $event)"
              >
                <el-option
                  v-for="header in sourceHeaders"
                  :key="header.index"
                  :label="getHeaderOptionLabel(header)"
                  :value="header.index"
                  :disabled="isHeaderUsed(header.index, scope.row.key)"
                />
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="源数据样例" min-width="260">
            <template slot-scope="scope">
              <span v-if="getMappedSamples(scope.row.key).length" class="sample-values">
                {{ getMappedSamples(scope.row.key).join(' / ') }}
              </span>
              <span v-else class="empty-text">未匹配</span>
            </template>
          </el-table-column>
          <el-table-column label="默认值" min-width="180">
            <template slot-scope="scope">
              <el-input
                :value="defaultValues[scope.row.key]"
                clearable
                size="small"
                placeholder="源值为空时使用"
                @input="setDefaultValue(scope.row.key, $event)"
              />
            </template>
          </el-table-column>
          <el-table-column label="差异值转换" min-width="190">
            <template slot-scope="scope">
              <div class="value-mapping-control">
                <el-switch
                  :value="Boolean(hasMapping(scope.row.key) && valueMappingEnabled[scope.row.key])"
                  :disabled="!hasMapping(scope.row.key)"
                  active-text="开启"
                  @change="setValueMappingEnabled(scope.row.key, $event)"
                />
                <el-button
                  v-if="valueMappingEnabled[scope.row.key] && hasMapping(scope.row.key)"
                  type="text"
                  size="small"
                  icon="el-icon-setting"
                  @click="openValueMappingDialog(scope.row.key)"
                >
                  配置 {{ getSourceValueStats(scope.row.key).length }} 种
                </el-button>
                <span v-else-if="!hasMapping(scope.row.key)" class="empty-text">先选择源列</span>
              </div>
              <span v-if="getUnmappedValueCount(scope.row.key)" class="warning-text mapping-warning">
                {{ getUnmappedValueCount(scope.row.key) }} 种未转换
              </span>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <section v-else-if="activeStep === 2" class="step-section">
        <div class="match-summary">
          <span>源数据 <strong>{{ normalizedRows.length }}</strong></span>
          <span class="success-text">已匹配 <strong>{{ matchedRows.length }}</strong></span>
          <span class="danger-text">未匹配 <strong>{{ unmatchedRows.length }}</strong></span>
          <span class="warning-text">已忽略 <strong>{{ ignoredRows.length }}</strong></span>
          <span>房屋档案 <strong>{{ allRooms.length }}</strong></span>
          <div class="match-actions">
            <el-button
              size="small"
              icon="el-icon-download"
              :disabled="!unmatchedRows.length"
              @click="exportRowsToExcel(unmatchedRows, '未匹配房屋')"
            >
              导出未匹配
            </el-button>
            <el-button
              size="small"
              icon="el-icon-download"
              :disabled="!ignoredRows.length"
              @click="exportRowsToExcel(ignoredRows, '已忽略欠费')"
            >
              导出已忽略
            </el-button>
          </div>
        </div>
        <el-tabs v-model="matchTab">
          <el-tab-pane :label="`已匹配房屋（${matchedRows.length}）`" name="matched">
            <el-table :data="matchedRows" border stripe max-height="500">
              <el-table-column prop="sourceRowNumber" label="源行" width="75" align="center" />
              <el-table-column prop="sourceRoomText" label="源房屋" min-width="150" show-overflow-tooltip />
              <el-table-column label="系统房屋" min-width="150">
                <template slot-scope="scope">{{ getSystemRoomName(scope.row.systemRoom) }}</template>
              </el-table-column>
              <el-table-column label="房屋业主" min-width="170">
                <template slot-scope="scope">
                  <span>{{ scope.row.systemRoom.ownerName || '-' }}</span>
                  <el-tag v-if="scope.row.systemRoom.ownerName && (scope.row.systemRoom.ownerTypeName || scope.row.systemRoom.ownerTypeCd)" size="mini" type="info" effect="plain" class="owner-type-tag">
                    {{ scope.row.systemRoom.ownerTypeName || scope.row.systemRoom.ownerTypeCd }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="表格名称" min-width="140" show-overflow-tooltip>
                <template slot-scope="scope">{{ scope.row.ownerName || '-' }}</template>
              </el-table-column>
              <el-table-column label="房屋业主手机号" width="155" show-overflow-tooltip>
                <template slot-scope="scope">
                  <span :class="{ 'warning-text': scope.row.phoneMatchStatus === 'different' }">
                    {{ scope.row.systemRoom.link || '-' }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="表格手机号" width="155" show-overflow-tooltip>
                <template slot-scope="scope">
                  <span :class="{ 'warning-text': scope.row.phoneMatchStatus === 'different' }">
                    {{ scope.row.ownerTel || '-' }}
                  </span>
                  <el-tag
                    v-if="scope.row.phoneMatchStatus"
                    :type="phoneMatchTagType(scope.row.phoneMatchStatus)"
                    size="mini"
                    effect="plain"
                    class="phone-match-tag"
                  >
                    {{ phoneMatchLabel(scope.row.phoneMatchStatus) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="建筑面积" width="110" align="right">
                <template slot-scope="scope">{{ scope.row.systemRoom.builtUpArea || '-' }} ㎡</template>
              </el-table-column>
              <el-table-column prop="sourceTotal" label="源欠费总额" width="125" align="right">
                <template slot-scope="scope">{{ formatMoney(scope.row.sourceTotal) }}</template>
              </el-table-column>
              <el-table-column label="欠费期间" min-width="170">
                <template slot-scope="scope">{{ formatMonthRange(scope.row.months) }}</template>
              </el-table-column>
              <el-table-column label="匹配提示" min-width="260">
                <template slot-scope="scope">
                  <span v-if="!scope.row.warnings.length" class="success-text">匹配通过</span>
                  <template v-else>
                    <el-tag type="warning" size="mini" effect="plain">需核对</el-tag>
                    <span class="warning-text status-message">{{ scope.row.warnings.join('；') }}</span>
                  </template>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="150" fixed="right">
                <template slot-scope="scope">
                  <el-button type="text" @click="openManualRoom(scope.row, true)">更换房屋</el-button>
                  <el-button type="text" icon="el-icon-delete" class="danger-text" @click="ignoreMatchedRow(scope.row)">忽略</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
          <el-tab-pane :label="`未匹配房屋（${unmatchedRows.length}）`" name="unmatched">
            <el-table :data="unmatchedRows" border stripe max-height="500">
              <el-table-column prop="sourceRowNumber" label="源行" width="75" align="center" />
              <el-table-column prop="sourceRoomText" label="源房屋" min-width="160" show-overflow-tooltip />
              <el-table-column prop="ownerName" label="源业主" width="120" show-overflow-tooltip />
              <el-table-column label="源欠费总额" width="125" align="right">
                <template slot-scope="scope">{{ formatMoney(scope.row.sourceTotal) }}</template>
              </el-table-column>
              <el-table-column prop="matchReason" label="未匹配原因" min-width="300" show-overflow-tooltip />
              <el-table-column label="操作" width="110" fixed="right">
                <template slot-scope="scope">
                  <el-button type="primary" plain size="mini" @click="openManualRoom(scope.row, false)">手工匹配</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
          <el-tab-pane :label="`已忽略欠费（${ignoredRows.length}）`" name="ignored">
            <el-table :data="ignoredRows" border stripe max-height="500">
              <el-table-column prop="sourceRowNumber" label="源行" width="75" align="center" />
              <el-table-column prop="sourceRoomText" label="源房屋" min-width="150" show-overflow-tooltip />
              <el-table-column label="系统房屋" min-width="150" show-overflow-tooltip>
                <template slot-scope="scope">{{ getSystemRoomName(scope.row.systemRoom) }}</template>
              </el-table-column>
              <el-table-column prop="ownerName" label="表格业主" width="130" show-overflow-tooltip />
              <el-table-column label="房屋业主" width="145" show-overflow-tooltip>
                <template slot-scope="scope">{{ scope.row.systemRoom && scope.row.systemRoom.ownerName || '-' }}</template>
              </el-table-column>
              <el-table-column prop="ownerTel" label="表格手机号" width="155" show-overflow-tooltip />
              <el-table-column label="房屋业主手机号" width="155" show-overflow-tooltip>
                <template slot-scope="scope">{{ scope.row.systemRoom && scope.row.systemRoom.link || '-' }}</template>
              </el-table-column>
              <el-table-column label="源欠费总额" width="125" align="right">
                <template slot-scope="scope">{{ formatMoney(scope.row.sourceTotal) }}</template>
              </el-table-column>
              <el-table-column label="欠费期间" min-width="170">
                <template slot-scope="scope">{{ formatMonthRange(scope.row.months) }}</template>
              </el-table-column>
              <el-table-column prop="ignoreReason" label="忽略原因" min-width="150" show-overflow-tooltip />
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </section>

      <section v-else-if="activeStep === 3" class="step-section rule-section">
        <div class="rule-toolbar">
          <div>
            <strong>费用导入方式</strong>
            <el-radio-group v-model="amountMode" size="small" @change="handleAmountModeChange">
              <el-radio-button label="rules">按规则计算</el-radio-button>
              <el-radio-button label="dynamic-total">动态费用按合计导入</el-radio-button>
            </el-radio-group>
          </div>
        </div>
        <el-alert v-if="ruleErrors.length" type="warning" :closable="false" show-icon
          :title="ruleErrors.join('；')" class="step-alert" />
        <template v-if="isDirectTotalMode">
          <el-alert type="info" :closable="false" show-icon class="step-alert"
            title="每户直接导入表格的“单户欠费总金额”，无需面积、单价或逐月试算。每户生成一条覆盖原欠费期间的动态费用，不再叠加固定费用或做金额取整。" />
          <div class="rule-toolbar">
            <div>
              <strong>合计金额收费项</strong>
              <el-select v-model="totalFeeConfigId" filterable placeholder="请选择动态费用收费项" style="width:420px" @change="clearCalculationPreview">
                <el-option v-for="item in directTotalFeeConfigs" :key="item.configId" :label="feeConfigLabel(item)" :value="item.configId" />
              </el-select>
            </div>
            <span class="rule-range">欠费期间：{{ importMonthRange }}</span>
          </div>
        </template>
        <template v-else>
        <div class="rule-toolbar">
          <div>
            <strong>面积口径</strong>
            <el-radio-group v-model="areaBasis" size="small">
              <el-radio-button label="system">系统建筑面积</el-radio-button>
              <el-radio-button label="source-first">源表面积优先</el-radio-button>
            </el-radio-group>
          </div>
          <span class="rule-range">待计算周期：{{ importMonthRange }}</span>
        </div>
        <div class="rounding-config">
          <div>
            <strong>月度费用金额取整</strong>
            <span>控制每月各费用项及当月合计的计算精度</span>
          </div>
          <div class="rounding-controls">
            <span>取整方式</span>
            <el-select v-model="roundingConfig.scale" size="small" style="width: 140px">
              <el-option
                v-for="option in roundingScaleOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
            <span>保留位数</span>
            <el-select v-model="roundingConfig.decimalPlace" size="small" style="width: 140px">
              <el-option
                v-for="option in decimalPlaceOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </div>
        </div>
        <div class="rule-title">
          <div>
            <strong>物业费分段规则</strong>
            <span>物业费 = 建筑面积 × 当月单价</span>
          </div>
          <el-button type="primary" plain size="small" icon="el-icon-plus" @click="addPropertyRule">添加规则</el-button>
        </div>
        <el-table :data="propertyRules" border class="rule-table">
          <el-table-column label="开始月份" min-width="170">
            <template slot-scope="scope">
              <el-date-picker v-model="scope.row.startMonth" type="month" value-format="yyyy-MM" placeholder="开始月份" style="width:100%" />
            </template>
          </el-table-column>
          <el-table-column label="结束月份" min-width="170">
            <template slot-scope="scope">
              <el-date-picker v-model="scope.row.endMonth" type="month" value-format="yyyy-MM" placeholder="结束月份" style="width:100%" />
            </template>
          </el-table-column>
          <el-table-column label="单价（元/㎡/月）" min-width="170">
            <template slot-scope="scope">
              <el-input-number v-model="scope.row.rate" :min="0" :precision="4" :step="0.1" controls-position="right" style="width:100%" />
            </template>
          </el-table-column>
          <el-table-column label="绑定收费项" min-width="260">
            <template slot-scope="scope">
              <el-select v-model="scope.row.configId" filterable placeholder="请选择收费项" style="width:100%">
                <el-option v-for="item in feeRuleConfigs" :key="item.configId" :label="feeConfigLabel(item)" :value="item.configId" />
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80" align="center">
            <template slot-scope="scope">
              <el-button type="text" class="danger-text" :disabled="propertyRules.length === 1" @click="removePropertyRule(scope.$index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="rule-title fixed-title">
          <div>
            <strong>其它固定费用</strong>
            <span>在指定月份内按户、按月增加固定金额，例如固定公摊费</span>
          </div>
          <el-button type="success" plain size="small" icon="el-icon-plus" @click="addFixedFeeRule">添加费用项</el-button>
        </div>
        <el-empty v-if="!fixedFeeRules.length" description="暂未添加其它固定费用" :image-size="58" />
        <el-table v-else :data="fixedFeeRules" border class="rule-table">
          <el-table-column label="费用名称" min-width="160">
            <template slot-scope="scope"><el-input v-model.trim="scope.row.name" placeholder="如：固定公摊费" /></template>
          </el-table-column>
          <el-table-column label="开始月份" min-width="160">
            <template slot-scope="scope">
              <el-date-picker v-model="scope.row.startMonth" type="month" value-format="yyyy-MM" placeholder="开始月份" style="width:100%" />
            </template>
          </el-table-column>
          <el-table-column label="结束月份" min-width="160">
            <template slot-scope="scope">
              <el-date-picker v-model="scope.row.endMonth" type="month" value-format="yyyy-MM" placeholder="结束月份" style="width:100%" />
            </template>
          </el-table-column>
          <el-table-column label="固定金额（元/月）" min-width="170">
            <template slot-scope="scope">
              <el-input-number v-model="scope.row.amount" :min="0" :precision="2" :step="1" controls-position="right" style="width:100%" />
            </template>
          </el-table-column>
          <el-table-column label="绑定收费项" min-width="240">
            <template slot-scope="scope">
              <el-select v-model="scope.row.configId" filterable placeholder="请选择收费项" style="width:100%">
                <el-option v-for="item in feeRuleConfigs" :key="item.configId" :label="feeConfigLabel(item)" :value="item.configId" />
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80" align="center">
            <template slot-scope="scope">
              <el-button type="text" class="danger-text" @click="removeFixedFeeRule(scope.$index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        </template>
      </section>

      <section v-else class="step-section preview-section">
        <div class="preview-summary">
          <div><strong>{{ calculatedRows.length }}</strong><span>已匹配户</span></div>
          <div class="success-text"><strong>{{ validCalculationCount }}</strong><span>核对通过</span></div>
          <div class="danger-text"><strong>{{ invalidCalculationCount }}</strong><span>核对异常</span></div>
          <div><strong>{{ selectedCalculationCount }}</strong><span>选中导入</span></div>
          <div><strong>{{ outputRowCount }}</strong><span>欠费明细</span></div>
          <div><strong>{{ formatMoney(selectedSourceTotal) }}</strong><span>选中总额</span></div>
        </div>
        <div class="preview-rounding">{{ isDirectTotalMode ? '动态费用：按源表合计金额导入，每户一条，不做费用计算' : '月度金额取整：' + roundingDescription }}</div>
        <el-alert
          v-if="outputRowCount > maxImportDetailRows"
          type="error"
          :closable="false"
          show-icon
          :title="`拆分后的欠费明细超过当前接口单次 ${maxImportDetailRows} 条上限，请减少选中房屋或缩短计费周期`"
          class="step-alert"
        />
        <el-tabs v-model="previewTab">
          <el-tab-pane :label="`已匹配房屋（${calculatedRows.length}）`" name="calculated">
            <el-table :data="calculatedRows" border stripe max-height="490">
              <el-table-column label="导入" width="65" align="center">
                <template slot-scope="scope">
                  <el-checkbox v-model="scope.row.selected" :disabled="scope.row.calcStatus !== 'pass'" @change="refreshSelectionSummary" />
                </template>
              </el-table-column>
              <el-table-column prop="sourceRowNumber" label="源行" width="70" align="center" />
              <el-table-column label="系统房屋" min-width="150">
                <template slot-scope="scope">{{ getSystemRoomName(scope.row.systemRoom) }}</template>
              </el-table-column>
              <el-table-column label="房屋业主" min-width="155">
                <template slot-scope="scope">
                  <span>{{ scope.row.systemRoom.ownerName || '-' }}</span>
                  <el-tag v-if="scope.row.systemRoom.ownerName && (scope.row.systemRoom.ownerTypeName || scope.row.systemRoom.ownerTypeCd)" size="mini" type="info" effect="plain" class="owner-type-tag">
                    {{ scope.row.systemRoom.ownerTypeName || scope.row.systemRoom.ownerTypeCd }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="表格名称" width="125" show-overflow-tooltip>
                <template slot-scope="scope">{{ scope.row.ownerName || '-' }}</template>
              </el-table-column>
              <el-table-column v-if="!isDirectTotalMode" label="计费面积" width="105" align="right">
                <template slot-scope="scope">{{ scope.row.calculation ? scope.row.calculation.area : '-' }} ㎡</template>
              </el-table-column>
              <el-table-column label="欠费期间" min-width="150">
                <template slot-scope="scope">{{ formatMonthRange(scope.row.months) }}</template>
              </el-table-column>
              <el-table-column label="源总额" width="110" align="right">
                <template slot-scope="scope">{{ formatMoney(scope.row.sourceTotal) }}</template>
              </el-table-column>
              <el-table-column :label="isDirectTotalMode ? '导入合计' : '计算总额'" width="110" align="right">
                <template slot-scope="scope">{{ scope.row.calculation ? formatBillingMoney(scope.row.calculation.total) : '-' }}</template>
              </el-table-column>
              <el-table-column label="差额" width="180" align="right">
                <template slot-scope="scope">
                  <div class="difference-display">
                    <span :class="scope.row.calcStatus === 'pass' ? 'success-text' : 'danger-text'">
                      {{ scope.row.calculation ? formatMoney(scope.row.calculation.difference) : '-' }}
                    </span>
                    <el-tag
                      v-if="scope.row.calculation && scope.row.calculation.balanceOffset && scope.row.calculation.balanceOffset.applied > 0"
                      type="warning"
                      size="mini"
                      effect="plain"
                    >
                      已冲正{{ formatMoney(scope.row.calculation.balanceOffset.applied) }}
                    </el-tag>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="状态" min-width="230">
                <template slot-scope="scope">
                  <el-tag v-if="scope.row.calcStatus === 'pass'" type="success" size="mini">可导入</el-tag>
                  <el-tag v-else type="danger" size="mini">不可导入</el-tag>
                  <el-tag
                    v-if="scope.row.calculation && scope.row.calculation.balanceOffset && scope.row.calculation.balanceOffset.applied > 0"
                    type="warning"
                    size="mini"
                    effect="plain"
                  >
                    余额冲正 {{ formatMoney(scope.row.calculation.balanceOffset.applied) }} 元
                  </el-tag>
                  <el-tag v-if="scope.row.warnings.length" type="warning" size="mini" effect="plain">需核对</el-tag>
                  <span class="status-message">{{ scope.row.calcMessage }}</span>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="180" fixed="right">
                <template slot-scope="scope">
                  <el-tooltip v-if="!isDirectTotalMode" content="按最早月份依次冲抵当前计算差额" placement="top">
                    <el-button
                      type="text"
                      icon="el-icon-refresh"
                      :disabled="!canApplyBalanceOffset(scope.row)"
                      @click="applyCalculatedBalanceOffset(scope.row)"
                    >
                      余额冲正
                    </el-button>
                  </el-tooltip>
                  <el-button type="text" icon="el-icon-document" :disabled="!scope.row.calculation" @click="openCalculationDetail(scope.row)">欠费详情</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
          <el-tab-pane :label="`未匹配房屋（${unmatchedRows.length}）`" name="unmatched">
            <el-table :data="unmatchedRows" border stripe max-height="490">
              <el-table-column prop="sourceRowNumber" label="源行" width="75" align="center" />
              <el-table-column prop="sourceRoomText" label="源房屋" min-width="170" />
              <el-table-column prop="ownerName" label="源业主" width="120" />
              <el-table-column prop="matchReason" label="未匹配原因" min-width="320" />
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </section>

      <span slot="footer" class="dialog-footer">
        <span v-if="draftSavedAt" class="draft-status">最近暂存：{{ formatDraftTime(draftSavedAt) }}</span>
        <el-button
          :loading="savingDraft"
          :disabled="submitting || savingDraft || !hasDraftData"
          icon="el-icon-document"
          @click="saveDraft()"
        >
          暂存草稿
        </el-button>
        <el-button :disabled="submitting || savingDraft" @click="requestClose">取消</el-button>
        <el-button v-if="activeStep > 0" :disabled="submitting || matching" icon="el-icon-back" @click="previousStep">上一步</el-button>
        <el-button
          v-if="activeStep === 0 && sourceRows.length"
          type="primary"
          icon="el-icon-right"
          @click="activeStep = 1"
        >
          继续匹配字段
        </el-button>
        <el-button
          v-else-if="activeStep === 1"
          type="primary"
          :loading="matching"
          :disabled="mappingErrors.length > 0"
          @click="matchRooms"
        >
          匹配房屋
        </el-button>
        <el-button v-else-if="activeStep === 2" type="primary" :disabled="!matchedRows.length" @click="goToRules">配置计费规则</el-button>
        <el-button v-else-if="activeStep === 3" type="primary" :disabled="ruleErrors.length > 0" @click="calculatePreview">{{ isDirectTotalMode ? '核对导入金额' : '开始试算' }}</el-button>
        <el-button
          v-else-if="activeStep === 4"
          type="success"
          :loading="submitting"
          :disabled="!canSubmit"
          icon="el-icon-upload2"
          @click="submitImport"
        >
          确认导入 {{ selectedCalculationCount }} 户
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      :visible.sync="valueMappingDialogVisible"
      :title="activeValueMappingField ? activeValueMappingField.label + '差异值转换' : '差异值转换'"
      width="760px"
      append-to-body
      @closed="handleValueMappingDialogClosed"
    >
      <template v-if="activeValueMappingField">
        <div class="value-mapping-heading">
          <p>
            已统计 {{ activeValueMappingStats.length }} 种非空上传值
            <template v-if="activeValueMappingEmptyCount">
              ，另有 {{ activeValueMappingEmptyCount }} 条空值{{ hasDefaultValue(activeValueMappingField.key) ? '将使用默认值' : '保持为空' }}
            </template>
          </p>
          <p class="field-description">{{ activeValueMappingField.description }}</p>
        </div>
        <el-alert
          v-if="!activeValueMappingStats.length"
          title="当前源列没有可统计的非空数据"
          type="info"
          :closable="false"
          show-icon
        />
        <el-table v-else :data="activeValueMappingStats" border stripe size="mini" max-height="360">
          <el-table-column label="上传表格中的值" min-width="250">
            <template slot-scope="scope"><strong>{{ scope.row.value }}</strong></template>
          </el-table-column>
          <el-table-column label="出现次数" prop="count" width="110" align="center" />
          <el-table-column :label="'转换为系统' + activeValueMappingField.label" min-width="300">
            <template slot-scope="scope">
              <el-input
                :value="getValueMappingTarget(activeValueMappingField.key, scope.row.key)"
                clearable
                size="small"
                placeholder="请输入系统值"
                @input="setValueMappingTarget(activeValueMappingField.key, scope.row.key, $event)"
              />
            </template>
          </el-table-column>
        </el-table>
        <p class="value-mapping-note">每一种非空上传值都必须填写转换结果；未填写的值会标记为异常并阻止匹配。</p>
      </template>
    </el-dialog>

    <el-dialog
      :visible.sync="manualRoomVisible"
      title="手工匹配系统房屋"
      width="820px"
      append-to-body
    >
      <el-input
        v-model.trim="manualRoomKeyword"
        clearable
        prefix-icon="el-icon-search"
        placeholder="搜索房屋、业主或手机号"
        class="manual-search"
      />
      <el-table :data="manualRoomCandidates" border stripe max-height="430">
        <el-table-column label="房屋" min-width="180">
          <template slot-scope="scope">{{ getSystemRoomName(scope.row) }}</template>
        </el-table-column>
        <el-table-column prop="ownerName" label="业主" width="130" />
        <el-table-column prop="link" label="手机号" width="140" />
        <el-table-column prop="builtUpArea" label="建筑面积" width="110" align="right" />
        <el-table-column label="操作" width="85" align="center">
          <template slot-scope="scope"><el-button type="text" @click="applyManualRoom(scope.row)">选择</el-button></template>
        </el-table-column>
      </el-table>
      <div v-if="manualRoomCandidates.length >= 200" class="candidate-tip">当前仅展示前 200 条匹配结果，请输入更精确的关键词。</div>
    </el-dialog>

    <el-dialog
      :visible.sync="detailVisible"
      :title="detailRow ? `${getSystemRoomName(detailRow.systemRoom)} 欠费详情` : '欠费详情'"
      width="1280px"
      custom-class="history-owe-fee-detail-dialog"
      append-to-body
      @closed="resetDetailEditor"
    >
      <div v-if="detailRow && detailRow.calculation" class="detail-overview">
        <span>源总额：<strong>{{ formatMoney(detailRow.sourceTotal) }}</strong></span>
        <span>{{ isDirectTotalMode ? '导入合计' : '计算总额' }}：<strong>{{ formatBillingMoney(detailRow.calculation.total) }}</strong></span>
        <span>差额：<strong>{{ formatMoney(detailRow.calculation.difference) }}</strong></span>
        <span v-if="detailRow.calculation.balanceOffset && detailRow.calculation.balanceOffset.applied > 0">
          余额冲正：<strong class="balance-adjusted-amount">{{ formatMoney(detailRow.calculation.balanceOffset.applied) }}</strong>
        </span>
      </div>
      <el-table :data="detailItems" border stripe max-height="470">
        <el-table-column prop="month" :label="isDirectTotalMode ? '欠费期间' : '计费月份'" :width="isDirectTotalMode ? 220 : 105" />
        <el-table-column prop="configName" label="费用项目" min-width="170" />
        <el-table-column prop="typeName" label="费用类型" width="110" />
        <el-table-column prop="formula" label="计算依据" min-width="240" />
        <el-table-column label="欠费金额" min-width="230" align="right">
          <template slot-scope="scope">
            <el-input-number
              v-if="editingDetailKey === scope.row.key"
              v-model="editingDetailAmount"
              :min="0"
              :max="999999999"
              :precision="billingDecimalPlaces"
              :step="detailAmountStep"
              controls-position="right"
              size="small"
              class="detail-amount-input"
              @keyup.enter.native="saveDetailAmount(scope.row)"
            />
            <div v-else class="detail-amount-display">
              <span
                :class="{
                  'balance-adjusted-amount': scope.row.item.balanceOffsetAmount > 0,
                  'manual-edited-amount': scope.row.item.manuallyEdited
                }"
              >
                {{ formatBillingMoney(scope.row.item.amount) }}
              </span>
              <el-tag v-if="scope.row.item.balanceOffsetAmount > 0" type="warning" size="mini" effect="plain">余额冲正</el-tag>
              <el-tag v-if="scope.row.item.manuallyEdited" type="primary" size="mini" effect="plain">已编辑</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="isDirectTotalMode ? '期间合计' : '当月合计'" width="120" align="right">
          <template slot-scope="scope">{{ formatBillingMoney(scope.row.monthDetail.total) }}</template>
        </el-table-column>
        <el-table-column v-if="!isDirectTotalMode" label="操作" width="90" align="center" fixed="right">
          <template slot-scope="scope">
            <template v-if="editingDetailKey === scope.row.key">
              <el-tooltip content="保存金额" placement="top">
                <el-button type="text" icon="el-icon-check" class="success-text" @click="saveDetailAmount(scope.row)" />
              </el-tooltip>
              <el-tooltip content="取消编辑" placement="top">
                <el-button type="text" icon="el-icon-close" @click="resetDetailEditor" />
              </el-tooltip>
            </template>
            <el-tooltip v-else content="编辑欠费金额" placement="top">
              <el-button type="text" icon="el-icon-edit" @click="startEditDetail(scope.row)" />
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { queryRooms } from '@/api/room/roomApi'

const MAX_SOURCE_ROWS = 50000
const MAX_IMPORT_DETAIL_ROWS = 50000
const ROOM_PAGE_SIZE = 1000
const DRAFT_STORAGE_PREFIX = 'history-owe-fee-import-draft:'
const DRAFT_VERSION = 3
const DRAFT_DATABASE_NAME = 'property-management-local-drafts'
const DRAFT_STORE_NAME = 'history-owe-fee-import'
const IMPORT_TARGET_OPTIONS = [
  {
    value: 'ROOM',
    label: '普通住宅',
    roomType: '1010301',
    description: '仅查询并匹配普通住宅，商铺数据不会进入本次导入'
  },
  {
    value: 'SHOP',
    label: '商铺',
    roomType: '2020602',
    description: '仅查询并匹配商铺，普通住宅数据不会进入本次导入'
  }
]
const ROUNDING_SCALE_OPTIONS = [
  { value: '1', label: '四舍五入' },
  { value: '3', label: '向上取整' },
  { value: '4', label: '向下取整' }
]
const DECIMAL_PLACE_OPTIONS = [
  { value: '0', label: '整数' },
  { value: '1', label: '保留1位小数' },
  { value: '2', label: '保留2位小数' },
  { value: '3', label: '保留3位小数' },
  { value: '4', label: '保留4位小数' }
]
const PROPERTY_FEE_TYPE_CD = '888800010001'

const EMPTY_VALUE = value => value === undefined || value === null || String(value).trim() === ''

const isDynamicFeeConfig = item => String(item && item.computingFormula) === '4004'
const isPropertyFeeConfig = item => String(item && item.feeTypeCd) === PROPERTY_FEE_TYPE_CD
const isHistoryImportFeeConfig = item => isDynamicFeeConfig(item) || isPropertyFeeConfig(item)

const FIELD_DEFINITIONS = [
  {
    key: 'externalRoomCode', label: '房屋唯一编码', required: false,
    aliases: ['房屋ID', '房间ID', '房屋编码', '房产编码', '资源编码', '外部房屋编码'],
    description: '可匹配系统房屋ID；没有编码时可使用完整房屋或楼栋/单元/房号'
  },
  {
    key: 'roomName', label: '完整房屋信息', required: false,
    aliases: ['收费对象', '房屋', '房屋信息', '房屋编号', '房号全称', '期栋'],
    description: '房号匹配的辅助信息，也可单独作为兼容定位方式'
  },
  { key: 'floorNum', label: '楼栋', required: false, aliases: ['楼栋编号', '楼号', '大楼名称', '栋号'], description: '拆分房屋定位字段' },
  { key: 'unitNum', label: '单元', required: false, aliases: ['单元编号', '单元号'], description: '拆分房屋定位字段' },
  { key: 'roomNum', label: '房号', required: true, aliases: ['房间', '房间号', '房间编号', '门牌号'], description: '优先按房号匹配系统房屋；重复房号需结合楼栋/单元消歧' },
  { key: 'ownerName', label: '业主名称', required: false, aliases: ['业主', '客户名称', '住户名称', '联系人'], description: '辅助核对，系统当前业主作为最终落账业主' },
  { key: 'ownerTel', label: '手机号', required: false, aliases: ['联系电话', '电话', '手机号码', '业主电话', '联系方式'], description: '辅助核对，不作为唯一房屋定位条件' },
  {
    key: 'oweTotal', label: '单户欠费总金额', required: true,
    aliases: ['欠费总额', '欠费金额', '欠款金额', '累计欠费', '应收欠费', '合计欠费', '历史欠费', '合计', '总金额', '应收合计'],
    description: '必须大于0；动态费用可直接按此合计导入，按规则计算时需核对一致'
  },
  {
    key: 'latestOweMonth', label: '最晚欠费月份', required: true,
    aliases: ['最晚欠费时间', '欠费截止月份', '截止月份', '最后欠费月', '最新欠费月', '计费月份'],
    description: '与欠费月数成组使用，例如2026-08'
  },
  { key: 'oweMonths', label: '欠费月数', required: true, aliases: ['累欠月数', '欠费月份数', '欠费周期数', '月数'], description: '从最晚欠费月份向前逆推' },
  {
    key: 'periodStart', label: '计费开始时间', required: true,
    aliases: ['欠费开始时间', '开始时间', '起始时间', '开始日期', '欠费起始月份'],
    description: '与计费结束时间成组使用，可替代最晚月份+欠费月数'
  },
  {
    key: 'periodEnd', label: '计费结束时间', required: true,
    aliases: ['欠费结束时间', '结束时间', '截止时间', '结束日期', '欠费截止月份'],
    description: '与计费开始时间成组使用'
  },
  { key: 'sourceArea', label: '源表建筑面积', required: false, aliases: ['建筑面积', '计费面积', '面积'], description: '规则选择“源表面积优先”时使用' },
  { key: 'remark', label: '备注', required: false, aliases: ['说明', '备注信息', '其他'], description: '随导入来源写入欠费备注' }
]

export default {
  name: 'HistoryOweFeeImport',
  props: {
    communityId: {
      type: String,
      required: true
    },
    feeConfigs: {
      type: Array,
      default: () => []
    },
    submitHandler: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      dialogVisible: false,
      activeStep: 0,
      parsing: false,
      matching: false,
      submitting: false,
      savingDraft: false,
      draftSavedAt: '',
      importTargetType: '',
      workbook: null,
      sourceFile: null,
      sourceFileName: '',
      sheetNames: [],
      selectedSheetName: '',
      parsedSheetName: '',
      detectedHeaderRow: 1,
      sourceHeaders: [],
      sourceRows: [],
      mappings: {},
      defaultValues: {},
      valueMappingEnabled: {},
      valueMappings: {},
      activeValueMappingFieldKey: '',
      valueMappingDialogVisible: false,
      normalizedRows: [],
      allRooms: [],
      matchedRows: [],
      unmatchedRows: [],
      ignoredRows: [],
      matchTab: 'matched',
      previewTab: 'calculated',
      areaBasis: 'system',
      amountMode: 'rules',
      totalFeeConfigId: '',
      roundingConfig: {
        scale: '1',
        decimalPlace: '2'
      },
      propertyRules: [],
      fixedFeeRules: [],
      calculatedRows: [],
      selectionVersion: 0,
      manualRoomVisible: false,
      manualRoomKeyword: '',
      manualRoomRow: null,
      manualRoomFromMatched: false,
      detailVisible: false,
      detailRow: null,
      editingDetailKey: '',
      editingDetailAmount: 0
    }
  },
  computed: {
    fieldDefinitions() {
      return FIELD_DEFINITIONS
    },
    importTargetOptions() {
      return IMPORT_TARGET_OPTIONS
    },
    selectedImportTarget() {
      return IMPORT_TARGET_OPTIONS.find(option => option.value === this.importTargetType) || null
    },
    selectedRoomType() {
      return this.selectedImportTarget ? this.selectedImportTarget.roomType : ''
    },
    importTargetLabel() {
      return this.selectedImportTarget ? this.selectedImportTarget.label : '未选择'
    },
    importTargetDescription() {
      return this.selectedImportTarget
        ? this.selectedImportTarget.description
        : '每个导入批次只能处理一种房屋类型'
    },
    feeRuleConfigs() {
      return (this.feeConfigs || []).filter(item => item && item.configId && item.feeName && isHistoryImportFeeConfig(item))
    },
    isDirectTotalMode() {
      return this.amountMode === 'dynamic-total'
    },
    directTotalFeeConfigs() {
      return this.feeRuleConfigs.filter(isDynamicFeeConfig)
    },
    // 保留旧计算属性名称，避免已有草稿或外部调用受到影响。
    dynamicFeeConfigs() {
      return this.feeRuleConfigs
    },
    roundingScaleOptions() {
      return ROUNDING_SCALE_OPTIONS
    },
    decimalPlaceOptions() {
      return DECIMAL_PLACE_OPTIONS
    },
    sourceRowLimit() {
      return MAX_SOURCE_ROWS
    },
    hasDraftData() {
      return Boolean(
        this.sourceRows.length ||
        this.normalizedRows.length ||
        this.matchedRows.length ||
        this.unmatchedRows.length ||
        this.ignoredRows.length ||
        this.calculatedRows.length
      )
    },
    maxImportDetailRows() {
      return MAX_IMPORT_DETAIL_ROWS
    },
    billingDecimalPlaces() {
      const decimalPlace = Number(this.roundingConfig && this.roundingConfig.decimalPlace)
      return Number.isInteger(decimalPlace) && decimalPlace >= 0 && decimalPlace <= 4 ? decimalPlace : 2
    },
    detailAmountStep() {
      return 1 / Math.pow(10, this.billingDecimalPlaces)
    },
    roundingDescription() {
      const scale = ROUNDING_SCALE_OPTIONS.find(option => option.value === String(this.roundingConfig.scale))
      const decimalPlace = DECIMAL_PLACE_OPTIONS.find(option => option.value === String(this.roundingConfig.decimalPlace))
      return `${scale ? scale.label : '四舍五入'}，${decimalPlace ? decimalPlace.label : '保留2位小数'}`
    },
    activeValueMappingField() {
      return FIELD_DEFINITIONS.find(field => field.key === this.activeValueMappingFieldKey) || null
    },
    activeValueMappingStats() {
      return this.activeValueMappingField ? this.getSourceValueStats(this.activeValueMappingField.key) : []
    },
    activeValueMappingEmptyCount() {
      return this.activeValueMappingField ? this.getEmptySourceValueCount(this.activeValueMappingField.key) : 0
    },
    mappingErrors() {
      if (!this.sourceHeaders.length) return ['请先上传并解析表格']
      const errors = []
      const hasRoomLocator = this.hasFieldValue('externalRoomCode') || this.hasFieldValue('roomName') || this.hasFieldValue('roomNum') ||
        (this.hasFieldValue('floorNum') && this.hasFieldValue('unitNum') && this.hasFieldValue('roomNum'))
      const hasLatestPeriod = this.hasFieldValue('latestOweMonth') && this.hasFieldValue('oweMonths')
      const hasDatePeriod = this.hasFieldValue('periodStart') && this.hasFieldValue('periodEnd')
      if (!hasRoomLocator) errors.push('房号、房屋唯一编码、完整房屋信息或楼栋+单元+房号至少匹配一组')
      if (!this.hasFieldValue('oweTotal')) errors.push('必须匹配单户欠费总金额')
      if (!hasLatestPeriod && !hasDatePeriod) errors.push('最晚欠费月份+欠费月数、计费开始+结束时间至少匹配一组')
      FIELD_DEFINITIONS.forEach(field => {
        const count = this.getUnmappedValueCount(field.key)
        if (count > 0) errors.push(`${field.label}有${count}种源值未配置差异值转换`)
      })
      return errors
    },
    importMonthRange() {
      const months = this.matchedRows.reduce((result, row) => result.concat(row.months || []), [])
      if (!months.length) return '-'
      const sorted = Array.from(new Set(months)).sort()
      return `${sorted[0]} 至 ${sorted[sorted.length - 1]}`
    },
    ruleErrors() {
      const errors = []
      if (this.isDirectTotalMode) {
        if (!this.directTotalFeeConfigs.length) errors.push('当前小区没有动态费用收费项，请先维护收费项')
        if (!this.totalFeeConfigId) errors.push('请选择接收合计金额的动态费用收费项')
        else if (!this.directTotalFeeConfigs.some(item => item.configId === this.totalFeeConfigId)) {
          errors.push('合计金额只能绑定计算公式为“动态费用”的收费项')
        }
        return errors
      }
      if (!this.feeRuleConfigs.length) errors.push('当前小区没有可用于历史欠费计算的物业费或动态费用收费项')
      if (!this.propertyRules.length) errors.push('至少配置一条物业费规则')
      if (!ROUNDING_SCALE_OPTIONS.some(option => option.value === String(this.roundingConfig.scale))) {
        errors.push('月度费用金额取整方式无效')
      }
      if (!DECIMAL_PLACE_OPTIONS.some(option => option.value === String(this.roundingConfig.decimalPlace))) {
        errors.push('月度费用金额保留位数必须为0至4位')
      }
      this.propertyRules.forEach((rule, index) => {
        if (!rule.startMonth || !rule.endMonth) errors.push(`物业费规则${index + 1}未填写完整起止月份`)
        else if (rule.startMonth > rule.endMonth) errors.push(`物业费规则${index + 1}开始月份晚于结束月份`)
        if (!(Number(rule.rate) > 0)) errors.push(`物业费规则${index + 1}单价必须大于0`)
        if (!rule.configId) errors.push(`物业费规则${index + 1}未绑定收费项`)
        else if (!this.findFeeConfig(rule.configId)) errors.push(`物业费规则${index + 1}绑定的收费项不可用于历史欠费计算`)
      })
      const sortedPropertyRules = this.propertyRules.slice().filter(rule => rule.startMonth && rule.endMonth)
        .sort((a, b) => a.startMonth.localeCompare(b.startMonth))
      for (let i = 1; i < sortedPropertyRules.length; i += 1) {
        if (sortedPropertyRules[i].startMonth <= sortedPropertyRules[i - 1].endMonth) {
          errors.push('物业费分段规则存在重叠月份')
          break
        }
      }
      this.fixedFeeRules.forEach((rule, index) => {
        if (!rule.name) errors.push(`固定费用${index + 1}未填写名称`)
        if (!rule.startMonth || !rule.endMonth) errors.push(`固定费用“${rule.name || index + 1}”未填写完整起止月份`)
        else if (rule.startMonth > rule.endMonth) errors.push(`固定费用“${rule.name || index + 1}”开始月份晚于结束月份`)
        if (!(Number(rule.amount) > 0)) errors.push(`固定费用“${rule.name || index + 1}”金额必须大于0`)
        if (!rule.configId) errors.push(`固定费用“${rule.name || index + 1}”未绑定收费项`)
        else if (!this.findFeeConfig(rule.configId)) errors.push(`固定费用“${rule.name || index + 1}”绑定的收费项不可用于历史欠费计算`)
      })
      const propertyConfigIds = new Set(this.propertyRules.map(rule => rule.configId).filter(Boolean))
      this.fixedFeeRules.forEach(rule => {
        if (rule.configId && propertyConfigIds.has(rule.configId)) {
          errors.push(`固定费用“${rule.name || '未命名'}”不能与物业费共用同一收费项`)
        }
      })
      for (let i = 0; i < this.fixedFeeRules.length; i += 1) {
        for (let j = i + 1; j < this.fixedFeeRules.length; j += 1) {
          const first = this.fixedFeeRules[i]
          const second = this.fixedFeeRules[j]
          if (first.configId && first.configId === second.configId && this.rangesOverlap(first, second)) {
            errors.push(`固定费用“${first.name || i + 1}”与“${second.name || j + 1}”在重叠周期内共用收费项`)
          }
        }
      }
      return Array.from(new Set(errors))
    },
    validCalculationCount() {
      return this.calculatedRows.filter(row => row.calcStatus === 'pass').length
    },
    invalidCalculationCount() {
      return this.calculatedRows.length - this.validCalculationCount
    },
    selectedCalculationCount() {
      this.selectionVersion
      return this.selectedRows.length
    },
    selectedRows() {
      this.selectionVersion
      const matchedIds = new Set(this.matchedRows.map(row => row.id))
      return this.calculatedRows.filter(row => matchedIds.has(row.id) && row.calcStatus === 'pass' && row.selected)
    },
    outputRowCount() {
      return this.selectedRows.reduce((count, row) => {
        const details = row.calculation ? row.calculation.months : []
        return count + details.reduce((sum, month) => {
          return sum + month.items.filter(item => Number(item.amount) > 0).length
        }, 0)
      }, 0)
    },
    selectedSourceTotal() {
      return this.roundMoney(this.selectedRows.reduce((sum, row) => sum + Number(row.sourceTotal || 0), 0))
    },
    canSubmit() {
      return !this.submitting && !this.ruleErrors.length && this.selectedCalculationCount > 0 && this.outputRowCount > 0 && this.outputRowCount <= MAX_IMPORT_DETAIL_ROWS
    },
    manualRoomCandidates() {
      const keyword = this.normalizeSearchText(this.manualRoomKeyword)
      const rooms = keyword
        ? this.allRooms.filter(room => {
          const values = [this.getSystemRoomName(room), room.roomId, room.ownerName, room.link]
          return values.some(value => this.normalizeSearchText(value).includes(keyword))
        })
        : this.allRooms
      return rooms.slice(0, 200)
    },
    detailItems() {
      if (!this.detailRow || !this.detailRow.calculation) return []
      return this.detailRow.calculation.months.reduce((result, month, monthIndex) => {
        month.items.forEach((item, itemIndex) => {
          result.push({
            key: `${this.detailRow.id}-${monthIndex}-${itemIndex}`,
            month: item.type === 'dynamic-total' ? this.formatMonthRange(this.detailRow.months) : month.month,
            configName: item.configName,
            typeName: item.type === 'dynamic-total' ? '动态合计费用' : (item.type === 'property' ? '物业费' : '固定费用'),
            formula: item.type === 'dynamic-total' ? '直接使用源表单户欠费合计，不计算、不按月拆分' : item.type === 'property'
              ? `${item.area}㎡ × ${item.rate}元/㎡/月`
              : `${item.name} ${this.formatBillingMoney(item.baseAmount === undefined ? item.amount : item.baseAmount)}元/月`,
            item,
            monthDetail: month
          })
        })
        return result
      }, [])
    }
  },
  methods: {
    async open() {
      this.reset()
      let draft = null
      try {
        draft = await this.readDraft()
      } catch (error) {
        this.$message.error('本地草稿读取失败，将开始新的导入')
      }
      if (!draft) {
        this.dialogVisible = true
        return
      }
      if (!this.isDraftCompatible(draft)) {
        await this.clearDraft(false)
        this.$message.warning('检测到的本地草稿已失效，已开始新的导入')
        this.dialogVisible = true
        return
      }
      try {
        await this.$confirm(
          `检测到${this.formatDraftTime(draft.savedAt)}保存的历史欠费导入草稿，是否恢复？`,
          '恢复本地草稿',
          {
            type: 'info',
            confirmButtonText: '恢复草稿',
            cancelButtonText: '开始新导入',
            distinguishCancelAndClose: true
          }
        )
        this.restoreDraft(draft)
        this.dialogVisible = true
        this.$message.success('已恢复本地草稿')
      } catch (action) {
        if (action === 'cancel') {
          await this.clearDraft(false)
          this.reset()
          this.dialogVisible = true
        }
      }
    },
    draftStorageKey() {
      return `${DRAFT_STORAGE_PREFIX}${this.currentDraftUserId()}:${String(this.communityId || '')}`
    },
    currentDraftUserId() {
      try {
        const user = JSON.parse(window.localStorage.getItem('user') || '{}')
        return String(user && user.userId ? user.userId : 'anonymous')
      } catch (error) {
        return 'anonymous'
      }
    },
    openDraftDatabase() {
      return new Promise((resolve, reject) => {
        if (!window.indexedDB) {
          reject(new Error('当前浏览器不支持本地草稿存储'))
          return
        }
        const request = window.indexedDB.open(DRAFT_DATABASE_NAME, 1)
        request.onupgradeneeded = () => {
          const database = request.result
          if (!database.objectStoreNames.contains(DRAFT_STORE_NAME)) {
            database.createObjectStore(DRAFT_STORE_NAME)
          }
        }
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error || new Error('本地草稿数据库打开失败'))
        request.onblocked = () => reject(new Error('本地草稿数据库正在被其它页面占用'))
      })
    },
    async readDraft() {
      const database = await this.openDraftDatabase()
      return new Promise((resolve, reject) => {
        const transaction = database.transaction(DRAFT_STORE_NAME, 'readonly')
        const request = transaction.objectStore(DRAFT_STORE_NAME).get(this.draftStorageKey())
        request.onsuccess = () => resolve(request.result || null)
        request.onerror = () => reject(request.error || new Error('本地草稿读取失败'))
        transaction.oncomplete = () => database.close()
        transaction.onerror = () => database.close()
        transaction.onabort = () => database.close()
      })
    },
    async writeDraft(draft) {
      const database = await this.openDraftDatabase()
      return new Promise((resolve, reject) => {
        const transaction = database.transaction(DRAFT_STORE_NAME, 'readwrite')
        transaction.objectStore(DRAFT_STORE_NAME).put(draft, this.draftStorageKey())
        transaction.oncomplete = () => {
          database.close()
          resolve()
        }
        transaction.onerror = () => {
          database.close()
          reject(transaction.error || new Error('本地草稿写入失败'))
        }
        transaction.onabort = () => {
          database.close()
          reject(transaction.error || new Error('本地草稿写入已中止'))
        }
      })
    },
    async removeDraft() {
      const database = await this.openDraftDatabase()
      return new Promise((resolve, reject) => {
        const transaction = database.transaction(DRAFT_STORE_NAME, 'readwrite')
        transaction.objectStore(DRAFT_STORE_NAME).delete(this.draftStorageKey())
        transaction.oncomplete = () => {
          database.close()
          resolve()
        }
        transaction.onerror = () => {
          database.close()
          reject(transaction.error || new Error('本地草稿删除失败'))
        }
        transaction.onabort = () => {
          database.close()
          reject(transaction.error || new Error('本地草稿删除已中止'))
        }
      })
    },
    createDraftSnapshot() {
      return JSON.parse(JSON.stringify({
        version: DRAFT_VERSION,
        savedAt: new Date().toISOString(),
        userId: this.currentDraftUserId(),
        communityId: String(this.communityId || ''),
        importTargetType: this.importTargetType,
        roomType: this.selectedRoomType,
        activeStep: this.activeStep,
        sourceFileName: this.sourceFileName,
        sheetNames: this.sheetNames,
        selectedSheetName: this.selectedSheetName,
        parsedSheetName: this.parsedSheetName,
        detectedHeaderRow: this.detectedHeaderRow,
        sourceHeaders: this.sourceHeaders,
        sourceRows: this.sourceRows,
        mappings: this.mappings,
        defaultValues: this.defaultValues,
        valueMappingEnabled: this.valueMappingEnabled,
        valueMappings: this.valueMappings,
        normalizedRows: this.normalizedRows,
        allRooms: this.allRooms,
        matchedRows: this.matchedRows,
        unmatchedRows: this.unmatchedRows,
        ignoredRows: this.ignoredRows,
        matchTab: this.matchTab,
        previewTab: this.previewTab,
        areaBasis: this.areaBasis,
        amountMode: this.amountMode,
        totalFeeConfigId: this.totalFeeConfigId,
        roundingConfig: this.roundingConfig,
        propertyRules: this.propertyRules,
        fixedFeeRules: this.fixedFeeRules,
        calculatedRows: this.calculatedRows,
        selectionVersion: this.selectionVersion
      }))
    },
    async saveDraft(showMessage = true) {
      if (!this.hasDraftData) {
        if (showMessage) this.$message.warning('暂无可暂存的导入数据')
        return false
      }
      this.savingDraft = true
      try {
        const draft = this.createDraftSnapshot()
        await this.writeDraft(draft)
        this.draftSavedAt = draft.savedAt
        if (showMessage) this.$message.success('当前步骤和导入数据已暂存到本地')
        return true
      } catch (error) {
        this.$message.error(error && error.message ? error.message : '本地草稿保存失败')
        return false
      } finally {
        this.savingDraft = false
      }
    },
    async clearDraft(showMessage = false) {
      try {
        await this.removeDraft()
        this.draftSavedAt = ''
        if (showMessage) this.$message.success('本地草稿已清除')
        return true
      } catch (error) {
        if (showMessage) this.$message.error(error && error.message ? error.message : '本地草稿清除失败')
        return false
      }
    },
    isDraftCompatible(draft) {
      const arrayFields = [
        'sheetNames', 'sourceHeaders', 'sourceRows', 'normalizedRows', 'allRooms',
        'matchedRows', 'unmatchedRows', 'ignoredRows', 'propertyRules',
        'fixedFeeRules', 'calculatedRows'
      ]
      return Boolean(
        draft &&
        draft.version === DRAFT_VERSION &&
        Boolean(draft.savedAt) &&
        String(draft.userId || '') === this.currentDraftUserId() &&
        String(draft.communityId || '') === String(this.communityId || '') &&
        this.isValidImportTarget(draft.importTargetType, draft.roomType) &&
        arrayFields.every(field => Array.isArray(draft[field])) &&
        draft.mappings && typeof draft.mappings === 'object'
      )
    },
    restoreDraft(draft) {
      const defaultMappings = FIELD_DEFINITIONS.reduce((result, field) => {
        result[field.key] = null
        return result
      }, {})
      this.activeStep = Math.max(0, Math.min(4, Math.trunc(Number(draft.activeStep) || 0)))
      this.importTargetType = draft.importTargetType
      this.sourceFileName = draft.sourceFileName || ''
      this.sheetNames = draft.sheetNames.slice()
      this.selectedSheetName = draft.selectedSheetName || ''
      this.parsedSheetName = draft.parsedSheetName || ''
      this.detectedHeaderRow = Number(draft.detectedHeaderRow) || 1
      this.sourceHeaders = draft.sourceHeaders
      this.sourceRows = draft.sourceRows
      this.mappings = { ...defaultMappings, ...draft.mappings }
      this.initializeFieldSettings()
      this.defaultValues = { ...this.defaultValues, ...(draft.defaultValues || {}) }
      this.valueMappingEnabled = { ...this.valueMappingEnabled, ...(draft.valueMappingEnabled || {}) }
      this.valueMappings = { ...this.valueMappings, ...(draft.valueMappings || {}) }
      FIELD_DEFINITIONS.forEach(field => this.syncValueMappings(field.key))
      this.normalizedRows = draft.normalizedRows
      this.allRooms = draft.allRooms
      this.matchedRows = draft.matchedRows.map(row => ({
        ...row,
        balanceOffsetEnabled: Boolean(row.balanceOffsetEnabled)
      }))
      this.unmatchedRows = draft.unmatchedRows
      this.ignoredRows = draft.ignoredRows
      this.matchTab = ['matched', 'unmatched', 'ignored'].includes(draft.matchTab) ? draft.matchTab : 'matched'
      this.previewTab = ['calculated', 'unmatched'].includes(draft.previewTab) ? draft.previewTab : 'calculated'
      this.areaBasis = draft.areaBasis === 'source-first' ? 'source-first' : 'system'
      this.amountMode = draft.amountMode === 'dynamic-total' ? 'dynamic-total' : 'rules'
      this.totalFeeConfigId = draft.totalFeeConfigId || ''
      this.roundingConfig = {
        scale: '1',
        decimalPlace: '2',
        ...(draft.roundingConfig || {})
      }
      this.propertyRules = draft.propertyRules
      this.fixedFeeRules = draft.fixedFeeRules
      this.calculatedRows = draft.calculatedRows
      if (this.isDirectTotalMode && this.activeStep === 4) {
        const selectedIds = new Set(draft.calculatedRows.filter(row => row.selected).map(row => row.id))
        this.calculatedRows = this.matchedRows.map(row => {
          const result = this.calculateRow(row)
          result.selected = result.calcStatus === 'pass' && selectedIds.has(row.id)
          return result
        })
      }
      this.selectionVersion = (Number(draft.selectionVersion) || 0) + 1
      this.workbook = null
      this.sourceFile = null
      this.parsing = false
      this.matching = false
      this.submitting = false
      this.savingDraft = false
      this.manualRoomVisible = false
      this.manualRoomKeyword = ''
      this.manualRoomRow = null
      this.manualRoomFromMatched = false
      this.detailVisible = false
      this.detailRow = null
      this.editingDetailKey = ''
      this.editingDetailAmount = 0
      this.draftSavedAt = draft.savedAt || ''
    },
    formatDraftTime(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '上次'
      return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())} ${this.pad(date.getHours())}:${this.pad(date.getMinutes())}:${this.pad(date.getSeconds())}`
    },
    reset() {
      this.activeStep = 0
      this.parsing = false
      this.matching = false
      this.submitting = false
      this.savingDraft = false
      this.draftSavedAt = ''
      this.importTargetType = ''
      this.workbook = null
      this.sourceFile = null
      this.sourceFileName = ''
      this.sheetNames = []
      this.selectedSheetName = ''
      this.parsedSheetName = ''
      this.detectedHeaderRow = 1
      this.sourceHeaders = []
      this.sourceRows = []
      this.mappings = FIELD_DEFINITIONS.reduce((result, field) => {
        result[field.key] = null
        return result
      }, {})
      this.initializeFieldSettings()
      this.normalizedRows = []
      this.allRooms = []
      this.matchedRows = []
      this.unmatchedRows = []
      this.ignoredRows = []
      this.matchTab = 'matched'
      this.previewTab = 'calculated'
      this.areaBasis = 'system'
      this.amountMode = 'rules'
      this.totalFeeConfigId = ''
      this.roundingConfig = {
        scale: '1',
        decimalPlace: '2'
      }
      this.propertyRules = [this.createPropertyRule()]
      this.fixedFeeRules = []
      this.calculatedRows = []
      this.selectionVersion = 0
      this.manualRoomVisible = false
      this.manualRoomKeyword = ''
      this.manualRoomRow = null
      this.manualRoomFromMatched = false
      this.detailVisible = false
      this.detailRow = null
      this.editingDetailKey = ''
      this.editingDetailAmount = 0
      this.$nextTick(() => {
        if (this.$refs.upload) this.$refs.upload.clearFiles()
      })
    },
    async handleImportTargetChange(nextTargetType) {
      const nextTarget = IMPORT_TARGET_OPTIONS.find(option => option.value === nextTargetType)
      if (!nextTarget || nextTarget.value === this.importTargetType) return
      if (!this.hasDraftData) {
        this.importTargetType = nextTarget.value
        return
      }
      try {
        await this.$confirm(
          `切换为${nextTarget.label}将清空当前文件、匹配结果、计费规则和试算数据，是否继续？`,
          '切换导入类型',
          {
            type: 'warning',
            confirmButtonText: '清空并切换',
            cancelButtonText: '取消'
          }
        )
        const draftCleared = await this.clearDraft(false)
        if (!draftCleared) {
          this.$message.error('本地草稿清除失败，暂未切换导入类型')
          return
        }
        this.reset()
        this.importTargetType = nextTarget.value
        this.dialogVisible = true
      } catch (error) {
        // 用户取消时保持原导入类型和当前数据。
      }
    },
    requestClose() {
      this.handleBeforeClose(() => {
        this.dialogVisible = false
      })
    },
    handleBeforeClose(done) {
      if (this.submitting || this.savingDraft) return
      if (!this.hasDraftData) {
        done()
        return
      }
      this.$confirm('是否暂存当前步骤和全部导入数据后关闭？', '关闭历史欠费导入', {
        type: 'warning',
        confirmButtonText: '暂存并关闭',
        cancelButtonText: '直接关闭',
        distinguishCancelAndClose: true
      }).then(async() => {
        const saved = await this.saveDraft(false)
        if (saved) done()
      }).catch(action => {
        if (action === 'cancel') done()
      })
    },
    async handleFileChange(uploadFile) {
      if (!this.selectedImportTarget) {
        this.$message.warning('请先选择本次导入普通住宅或商铺')
        this.$nextTick(() => {
          if (this.$refs.upload) this.$refs.upload.clearFiles()
        })
        return
      }
      const file = uploadFile && uploadFile.raw
      if (!file) return
      if (!/\.xlsx?$/i.test(file.name || '')) {
        this.$message.error('仅支持 .xls、.xlsx 文件')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('文件不能超过 10 MB')
        return
      }
      this.parsing = true
      try {
        const buffer = await this.readFile(file)
        const workbook = XLSX.read(buffer, { type: 'array', cellDates: true })
        if (!workbook.SheetNames.length) throw new Error('工作簿中没有可读取的工作表')
        this.workbook = Object.freeze(workbook)
        this.sourceFile = file
        this.sourceFileName = file.name
        this.sheetNames = workbook.SheetNames.slice()
        this.selectedSheetName = workbook.SheetNames[0]
        this.parseSelectedSheet()
        this.activeStep = 1
      } catch (error) {
        this.$message.error(error && error.message ? error.message : 'Excel 文件解析失败')
      } finally {
        this.parsing = false
        this.$nextTick(() => {
          if (this.$refs.upload) this.$refs.upload.clearFiles()
        })
      }
    },
    readFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = event => resolve(event.target.result)
        reader.onerror = () => reject(new Error('无法读取所选文件'))
        reader.readAsArrayBuffer(file)
      })
    },
    parseSelectedSheet() {
      const sheet = this.workbook && this.workbook.Sheets[this.selectedSheetName]
      if (!sheet) throw new Error('所选工作表不存在')
      const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false, blankrows: true })
      const headerIndex = this.detectHeaderIndex(matrix)
      if (matrix.length <= headerIndex) throw new Error('未找到有效表头')
      const headerCells = matrix[headerIndex] || []
      let lastHeaderIndex = headerCells.length - 1
      while (lastHeaderIndex >= 0 && EMPTY_VALUE(headerCells[lastHeaderIndex])) lastHeaderIndex -= 1
      if (lastHeaderIndex < 0) throw new Error('表头行没有有效字段')
      const columnCount = lastHeaderIndex + 1
      const nameCounts = {}
      let sourceHeaders = Array.from({ length: columnCount }, (_, index) => {
        const name = String(headerCells[index] === undefined ? '' : headerCells[index]).trim()
        const baseName = name || `${XLSX.utils.encode_col(index)}列（无表头）`
        nameCounts[baseName] = (nameCounts[baseName] || 0) + 1
        return { index, name, baseName, displayName: baseName }
      })
      sourceHeaders = sourceHeaders.map(header => ({
        ...header,
        displayName: nameCounts[header.baseName] > 1
          ? `${header.baseName}（${XLSX.utils.encode_col(header.index)}列）`
          : header.baseName
      }))
      const normalizedHeaders = headerCells.slice(0, columnCount).map(value => this.normalizeHeader(value))
      const rows = []
      for (let index = headerIndex + 1; index < matrix.length; index += 1) {
        const values = (matrix[index] || []).slice(0, columnCount)
        if (!values.some(value => !EMPTY_VALUE(value))) continue
        const first = values.find(value => !EMPTY_VALUE(value))
        if (/^(合计|总计)/.test(String(first || '').trim().replace(/\s+/g, ''))) break
        const repeatedCount = normalizedHeaders.reduce((count, header, colIndex) => {
          return count + (header && this.normalizeHeader(values[colIndex]) === header ? 1 : 0)
        }, 0)
        if (repeatedCount >= Math.max(2, Math.ceil(normalizedHeaders.filter(Boolean).length * 0.6))) continue
        rows.push({ sourceRowNumber: index + 1, values })
      }
      if (!rows.length) throw new Error('工作表中没有有效数据行')
      if (rows.length > MAX_SOURCE_ROWS) throw new Error(`源数据超过 ${MAX_SOURCE_ROWS} 条，请拆分文件后重试`)
      this.sourceHeaders = sourceHeaders
      this.sourceRows = rows
      this.detectedHeaderRow = headerIndex + 1
      this.parsedSheetName = this.selectedSheetName
      this.initializeFieldSettings()
      this.autoMatchMappings(false)
    },
    handleSheetChange() {
      try {
        this.parseSelectedSheet()
      } catch (error) {
        this.selectedSheetName = this.parsedSheetName
        this.$message.error(error && error.message ? error.message : '工作表解析失败')
      }
    },
    detectHeaderIndex(matrix) {
      const candidates = FIELD_DEFINITIONS.map(field => [field.label, field.key].concat(field.aliases || [])
        .map(value => this.normalizeHeader(value)).filter(Boolean))
      let best = { index: 0, score: -1 }
      const limit = Math.min(matrix.length, 50)
      for (let rowIndex = 0; rowIndex < limit; rowIndex += 1) {
        const cells = (matrix[rowIndex] || []).map(value => this.normalizeHeader(value)).filter(Boolean)
        if (cells.length < 2) continue
        let exact = 0
        let fuzzy = 0
        candidates.forEach(names => {
          if (names.some(name => cells.includes(name))) exact += 1
          else if (names.some(name => cells.some(cell => cell.length >= 2 && (cell.includes(name) || name.includes(cell))))) fuzzy += 1
        })
        const score = exact * 20 + fuzzy * 5 + Math.min(cells.length, 12)
        if (score > best.score) best = { index: rowIndex, score }
      }
      return best.score >= 20 ? best.index : 0
    },
    normalizeHeader(value) {
      return String(value === undefined || value === null ? '' : value)
        .trim().toLowerCase().replace(/[\s_\-—–()（）【】\u005b\u005d:：/\\]/g, '')
    },
    autoMatchMappings(showMessage) {
      const mappings = FIELD_DEFINITIONS.reduce((result, field) => {
        result[field.key] = null
        return result
      }, {})
      const used = new Set()
      FIELD_DEFINITIONS.forEach(field => {
        const names = [field.label, field.key].concat(field.aliases || []).map(value => this.normalizeHeader(value))
        let header = this.sourceHeaders.find(item => !used.has(item.index) && names.includes(this.normalizeHeader(item.name)))
        if (!header) {
          header = this.sourceHeaders.find(item => !used.has(item.index) && names.some(name => {
            const source = this.normalizeHeader(item.name)
            return source.length >= 2 && name.length >= 2 && (source.includes(name) || name.includes(source))
          }))
        }
        if (header) {
          mappings[field.key] = header.index
          used.add(header.index)
        }
      })
      this.mappings = mappings
      FIELD_DEFINITIONS.forEach(field => this.syncValueMappings(field.key))
      if (showMessage) this.$message.success('已根据当前表头重新匹配字段')
    },
    initializeFieldSettings() {
      const defaultValues = {}
      const valueMappingEnabled = {}
      const valueMappings = {}
      FIELD_DEFINITIONS.forEach(field => {
        defaultValues[field.key] = field.defaultValue === undefined || field.defaultValue === null
          ? ''
          : field.defaultValue
        valueMappingEnabled[field.key] = false
        valueMappings[field.key] = {}
      })
      this.defaultValues = defaultValues
      this.valueMappingEnabled = valueMappingEnabled
      this.valueMappings = valueMappings
      this.activeValueMappingFieldKey = ''
      this.valueMappingDialogVisible = false
    },
    setFieldMapping(fieldKey, sourceIndex) {
      const normalizedIndex = sourceIndex === '' || sourceIndex === null || sourceIndex === undefined
        ? null
        : sourceIndex
      this.$set(this.mappings, fieldKey, normalizedIndex)
      if (normalizedIndex === null && this.activeValueMappingFieldKey === fieldKey) {
        this.activeValueMappingFieldKey = ''
        this.valueMappingDialogVisible = false
      }
      this.syncValueMappings(fieldKey)
    },
    hasMapping(fieldKey) {
      return this.mappings[fieldKey] !== null && this.mappings[fieldKey] !== undefined
    },
    hasDefaultValue(fieldKey) {
      return !EMPTY_VALUE(this.defaultValues[fieldKey])
    },
    hasFieldValue(fieldKey) {
      return this.hasMapping(fieldKey) || this.hasDefaultValue(fieldKey)
    },
    setDefaultValue(fieldKey, value) {
      this.$set(this.defaultValues, fieldKey, value === null || value === undefined ? '' : value)
    },
    isHeaderUsed(sourceIndex, fieldKey) {
      return Object.keys(this.mappings).some(key => key !== fieldKey && this.mappings[key] === sourceIndex)
    },
    getHeaderOptionLabel(header) {
      const samples = this.sourceRows.map(row => row.values[header.index]).filter(value => !EMPTY_VALUE(value)).slice(0, 2)
      return samples.length ? `${header.displayName} · ${samples.join(' / ')}` : header.displayName
    },
    getMappedSamples(fieldKey) {
      if (!this.hasMapping(fieldKey)) return []
      const index = this.mappings[fieldKey]
      const samples = []
      this.sourceRows.forEach(row => {
        const text = String(row.values[index] === undefined ? '' : row.values[index]).trim()
        if (text && !samples.includes(text) && samples.length < 3) samples.push(text.length > 24 ? `${text.slice(0, 24)}...` : text)
      })
      return samples
    },
    normalizeSourceValue(value) {
      return EMPTY_VALUE(value) ? '' : String(value).trim()
    },
    sourceValueKey(value) {
      return JSON.stringify(this.normalizeSourceValue(value))
    },
    getSourceValueStats(fieldKey) {
      if (!this.hasMapping(fieldKey)) return []
      const sourceIndex = this.mappings[fieldKey]
      const stats = new Map()
      this.sourceRows.forEach(row => {
        const value = this.normalizeSourceValue(row.values[sourceIndex])
        if (!value) return
        const key = this.sourceValueKey(value)
        const current = stats.get(key)
        if (current) current.count += 1
        else stats.set(key, { key, value, count: 1 })
      })
      return Array.from(stats.values())
    },
    getEmptySourceValueCount(fieldKey) {
      if (!this.hasMapping(fieldKey)) return 0
      const sourceIndex = this.mappings[fieldKey]
      return this.sourceRows.reduce((count, row) => count + (EMPTY_VALUE(row.values[sourceIndex]) ? 1 : 0), 0)
    },
    setValueMappingEnabled(fieldKey, enabled) {
      if (enabled && !this.hasMapping(fieldKey)) return
      this.$set(this.valueMappingEnabled, fieldKey, Boolean(enabled))
      if (enabled) this.syncValueMappings(fieldKey)
      else if (this.activeValueMappingFieldKey === fieldKey) this.valueMappingDialogVisible = false
    },
    openValueMappingDialog(fieldKey) {
      if (!this.hasMapping(fieldKey)) return
      this.syncValueMappings(fieldKey)
      this.activeValueMappingFieldKey = fieldKey
      this.valueMappingDialogVisible = true
    },
    handleValueMappingDialogClosed() {
      this.activeValueMappingFieldKey = ''
    },
    syncValueMappings(fieldKey) {
      if (!this.hasMapping(fieldKey)) {
        this.$set(this.valueMappings, fieldKey, {})
        return
      }
      const currentMappings = this.valueMappings[fieldKey] || {}
      const nextMappings = {}
      this.getSourceValueStats(fieldKey).forEach(stat => {
        nextMappings[stat.key] = Object.prototype.hasOwnProperty.call(currentMappings, stat.key)
          ? currentMappings[stat.key]
          : stat.value
      })
      this.$set(this.valueMappings, fieldKey, nextMappings)
    },
    getValueMappingTarget(fieldKey, sourceKey) {
      const fieldMappings = this.valueMappings[fieldKey] || {}
      return Object.prototype.hasOwnProperty.call(fieldMappings, sourceKey) ? fieldMappings[sourceKey] : ''
    },
    setValueMappingTarget(fieldKey, sourceKey, targetValue) {
      const fieldMappings = { ...(this.valueMappings[fieldKey] || {}) }
      fieldMappings[sourceKey] = targetValue === null || targetValue === undefined ? '' : targetValue
      this.$set(this.valueMappings, fieldKey, fieldMappings)
    },
    getUnmappedValueCount(fieldKey) {
      if (!this.valueMappingEnabled[fieldKey] || !this.hasMapping(fieldKey)) return 0
      return this.getSourceValueStats(fieldKey).filter(stat => EMPTY_VALUE(this.getValueMappingTarget(fieldKey, stat.key))).length
    },
    resolveFieldValue(fieldKey, sourceRow) {
      const hasSource = this.hasMapping(fieldKey)
      const rawValue = hasSource ? sourceRow.values[this.mappings[fieldKey]] : ''
      let value = rawValue
      let defaultApplied = false
      if (EMPTY_VALUE(value) && this.hasDefaultValue(fieldKey)) {
        value = this.defaultValues[fieldKey]
        defaultApplied = true
      } else if (!EMPTY_VALUE(value) && hasSource && this.valueMappingEnabled[fieldKey]) {
        const sourceKey = this.sourceValueKey(value)
        const targetValue = this.getValueMappingTarget(fieldKey, sourceKey)
        if (EMPTY_VALUE(targetValue)) {
          return { value, rawValue, defaultApplied, error: `上传值“${this.normalizeSourceValue(value)}”尚未配置转换结果` }
        }
        value = targetValue
      }
      return { value, rawValue, defaultApplied, error: '' }
    },
    buildNormalizedRows() {
      return this.sourceRows.map(sourceRow => {
        const values = {}
        const errors = []
        FIELD_DEFINITIONS.forEach(field => {
          const resolved = this.resolveFieldValue(field.key, sourceRow)
          values[field.key] = this.normalizeSourceValue(resolved.value)
          if (resolved.error) errors.push(`${field.label}${resolved.error}`)
        })
        const splitRoomText = values.floorNum && values.unitNum && values.roomNum
          ? [values.floorNum, values.unitNum, values.roomNum].join('-')
          : ''
        const floorRoomText = values.floorNum && values.roomName ? `${values.floorNum}-${values.roomName}` : ''
        const sourceRoomText = splitRoomText || floorRoomText || values.roomName || values.roomNum || values.externalRoomCode
        if (!sourceRoomText) errors.push('缺少房屋定位信息')
        const sourceTotal = this.parseMoney(values.oweTotal)
        if (!(sourceTotal > 0)) errors.push('欠费总金额不是大于0的有效数字')
        const period = this.resolvePeriod(values)
        if (period.error) errors.push(period.error)
        const sourceArea = this.parseMoney(values.sourceArea)
        const sourceAreaError = values.sourceArea && !(sourceArea > 0) ? '源表建筑面积不是大于0的有效数字' : ''
        return {
          ...values,
          id: `source-${sourceRow.sourceRowNumber}`,
          sourceRowNumber: sourceRow.sourceRowNumber,
          sourceRoomText: sourceRoomText || '-',
          sourceTotal: sourceTotal || 0,
          sourceArea: sourceArea || 0,
          sourceAreaError,
          months: period.months || [],
          errors,
          matchReason: errors.join('；')
        }
      })
    },
    async matchRooms() {
      if (this.mappingErrors.length) return
      if (!this.selectedImportTarget) {
        this.$message.warning('请先选择本次导入普通住宅或商铺')
        this.activeStep = 0
        return
      }
      this.matching = true
      try {
        this.normalizedRows = this.buildNormalizedRows()
        await this.loadAllRooms()
        const roomIndexes = this.buildRoomIndexes()
        const matched = []
        const unmatched = []
        this.normalizedRows.forEach(row => {
          if (row.errors.length) {
            unmatched.push({ ...row, matchReason: `字段校验失败：${row.errors.join('；')}` })
            return
          }
          const matchResult = this.findMatchedRoom(row, roomIndexes)
          if (!matchResult.room) {
            unmatched.push({ ...row, matchReason: matchResult.reason })
            return
          }
          matched.push(this.createMatchedRow(row, matchResult.room, matchResult.reason))
        })
        this.matchedRows = matched
        this.unmatchedRows = unmatched
        this.ignoredRows = []
        this.calculatedRows = []
        this.refreshDuplicateRoomWarnings()
        this.matchTab = matched.length ? 'matched' : 'unmatched'
        this.activeStep = 2
      } catch (error) {
        this.$message.error(error && error.message ? error.message : '房屋匹配失败')
      } finally {
        this.matching = false
      }
    },
    async loadAllRooms() {
      const rooms = []
      let page = 1
      let total = 0
      do {
        const response = await queryRooms({
          communityId: this.communityId,
          roomType: this.selectedRoomType,
          page,
          row: ROOM_PAGE_SIZE
        })
        const batch = response.rooms || []
        const incompatibleRoom = batch.find(room => String(room.roomType || '') !== this.selectedRoomType)
        if (incompatibleRoom) {
          throw new Error(`房屋接口返回了非${this.importTargetLabel}数据，请刷新后重试`)
        }
        total = Number(response.total || 0)
        rooms.push(...batch)
        page += 1
        if (!batch.length) break
      } while (rooms.length < total)
      if (!rooms.length) throw new Error('当前小区没有可匹配的房屋档案')
      this.allRooms = rooms
    },
    buildRoomIndexes() {
      const byId = new Map()
      const byLocator = new Map()
      const byRoomNum = new Map()
      this.allRooms.forEach(room => {
        if (room.roomId) byId.set(String(room.roomId).trim(), room)
        const roomNumKey = this.normalizeRoomNum(this.getRoomPartValue(room, 'roomNum'))
        if (roomNumKey) {
          if (!byRoomNum.has(roomNumKey)) byRoomNum.set(roomNumKey, [])
          const roomList = byRoomNum.get(roomNumKey)
          if (!roomList.some(item => item.roomId === room.roomId)) roomList.push(room)
        }
        const names = [
          room.roomName,
          [
            this.getRoomPartValue(room, 'floorNum'),
            this.getRoomPartValue(room, 'unitNum'),
            this.getRoomPartValue(room, 'roomNum')
          ].filter(Boolean).join('-')
        ]
        names.forEach(name => {
          const key = this.normalizeRoomLocator(name)
          if (!key) return
          if (!byLocator.has(key)) byLocator.set(key, [])
          const roomList = byLocator.get(key)
          if (!roomList.some(item => item.roomId === room.roomId)) roomList.push(room)
        })
      })
      return { byId, byLocator, byRoomNum }
    },
    findMatchedRoom(row, indexes) {
      if (row.externalRoomCode && indexes.byId.has(row.externalRoomCode)) {
        return { room: indexes.byId.get(row.externalRoomCode), reason: '按系统房屋ID匹配' }
      }

      if (row.roomNum) {
        const roomNumCandidates = indexes.byRoomNum.get(this.normalizeRoomNum(row.roomNum)) || []
        const narrowed = roomNumCandidates.filter(room => {
          const floorMatched = this.roomPartMatches(row.floorNum, room, 'floorNum')
          const unitMatched = this.roomPartMatches(row.unitNum, room, 'unitNum')
          return floorMatched && unitMatched
        })
        if (narrowed.length === 1) {
          const reason = row.floorNum || row.unitNum ? '按楼栋/单元+房号匹配' : '按房号匹配'
          return { room: narrowed[0], reason }
        }
        if (narrowed.length > 1 && !row.roomName) {
          return { room: null, reason: `房号“${row.roomNum}”在指定楼栋/单元下匹配到${narrowed.length}个候选，请补充完整房屋信息` }
        }
        if (roomNumCandidates.length > 1 && !row.roomName) {
          return { room: null, reason: `房号“${row.roomNum}”匹配到多个候选，请补充楼栋或单元后重试` }
        }
      }

      const locatorTexts = [
        row.roomName,
        [row.floorNum, row.unitNum, row.roomNum].filter(Boolean).join('-'),
        row.floorNum && row.roomName ? `${row.floorNum}-${row.roomName}` : ''
      ].filter(Boolean)
      const candidates = []
      locatorTexts.forEach(text => {
        const rooms = indexes.byLocator.get(this.normalizeRoomLocator(text)) || []
        rooms.forEach(room => {
          const floorMatched = this.roomPartMatches(row.floorNum, room, 'floorNum')
          const unitMatched = this.roomPartMatches(row.unitNum, room, 'unitNum')
          if (floorMatched && unitMatched && !candidates.some(item => item.roomId === room.roomId)) candidates.push(room)
        })
      })
      if (candidates.length === 1) return { room: candidates[0], reason: '按标准化房屋名称匹配' }
      if (candidates.length > 1) return { room: null, reason: `房屋信息匹配到 ${candidates.length} 个候选，请手工选择` }
      return { room: null, reason: `系统中未找到房屋“${row.sourceRoomText}”` }
    },
    createMatchedRow(row, room, matchMode) {
      const warnings = []
      const sourceOwnerName = this.normalizeSearchText(row.ownerName)
      const systemOwnerName = this.normalizeSearchText(room.ownerName)
      if (sourceOwnerName && systemOwnerName && sourceOwnerName !== systemOwnerName) {
        warnings.push(`源业主“${row.ownerName}”与系统当前业主“${room.ownerName}”不一致`)
      }
      if (sourceOwnerName && !systemOwnerName) warnings.push(`表格名称“${row.ownerName}”未匹配到系统房屋业主`)
      if (!sourceOwnerName && systemOwnerName) warnings.push(`表格未提供业主名称，系统房屋业主为“${room.ownerName}”`)
      const phoneMatchStatus = this.getPhoneMatchStatus(row.ownerTel, room.link)
      if (phoneMatchStatus === 'different') warnings.push('源手机号与系统当前业主手机号不一致')
      if (row.sourceArea > 0 && Number(room.builtUpArea) > 0 && Math.abs(row.sourceArea - Number(room.builtUpArea)) > 0.01) {
        warnings.push(`源面积${row.sourceArea}㎡与系统面积${room.builtUpArea}㎡不一致`)
      }
      if (!room.ownerName) warnings.push('系统房屋未绑定业主，现有欠费接口无法提交')
      return {
        ...row,
        systemRoom: room,
        matchMode,
        warnings,
        phoneMatchStatus,
        duplicateRoom: false,
        calcStatus: 'pending',
        calcMessage: '待试算',
        calculation: null,
        balanceOffsetEnabled: Boolean(row.balanceOffsetEnabled),
        selected: false
      }
    },
    getPhoneMatchStatus(sourcePhone, systemPhone) {
      const sourceText = String(sourcePhone || '').trim()
      const systemText = String(systemPhone || '').trim()
      if (!sourceText && !systemText) return 'empty'
      if (!sourceText) return 'system-only'
      if (!systemText) return 'source-only'
      // 接口可能按权限返回掩码手机号，此时只能展示，不能据此判定不一致。
      if (/[＊*xX]/.test(sourceText) || /[＊*xX]/.test(systemText)) return 'masked'
      const sourceDigits = this.normalizePhone(sourceText)
      const systemDigits = this.normalizePhone(systemText)
      if (!sourceDigits || !systemDigits) return 'unverified'
      return sourceDigits.includes(systemDigits) ? 'same' : 'different'
    },
    phoneMatchLabel(status) {
      const labels = {
        same: '一致',
        different: '不一致',
        masked: '待核对',
        'source-only': '系统缺失',
        'system-only': '表格缺失',
        unverified: '待核对',
        empty: '未提供'
      }
      return labels[status] || '待核对'
    },
    phoneMatchTagType(status) {
      if (status === 'same') return 'success'
      if (status === 'different') return 'warning'
      if (status === 'empty') return 'info'
      return 'info'
    },
    refreshDuplicateRoomWarnings() {
      const counts = this.matchedRows.reduce((result, row) => {
        const roomId = row.systemRoom && row.systemRoom.roomId
        if (roomId) result[roomId] = (result[roomId] || 0) + 1
        return result
      }, {})
      this.matchedRows.forEach(row => {
        const duplicate = counts[row.systemRoom.roomId] > 1
        row.duplicateRoom = duplicate
        row.warnings = row.warnings.filter(message => !message.startsWith('同一系统房屋在源表出现'))
        if (duplicate) row.warnings.push(`同一系统房屋在源表出现${counts[row.systemRoom.roomId]}次，请先合并汇总数据`)
      })
    },
    ignoreMatchedRow(row) {
      this.$confirm('移除后该欠费信息不会参与试算和导入，是否继续？', '确认忽略', {
        type: 'warning'
      }).then(() => {
        this.ignoredRows.push({
          ...row,
          ignoreReason: '用户手动忽略'
        })
        this.matchedRows = this.matchedRows.filter(item => item.id !== row.id)
        this.calculatedRows = this.calculatedRows.filter(item => item.id !== row.id)
        this.refreshDuplicateRoomWarnings()
        if (!this.matchedRows.length) {
          this.matchTab = this.unmatchedRows.length ? 'unmatched' : 'ignored'
        }
        this.$message.success('已忽略该欠费信息')
      }).catch(() => {})
    },
    exportRowsToExcel(rows, fileName) {
      if (!rows || !rows.length) {
        this.$message.warning('暂无可导出的数据')
        return
      }
      const data = rows.map(row => ({
        源行: row.sourceRowNumber,
        源房屋: row.sourceRoomText || '',
        系统房屋: row.systemRoom ? this.getSystemRoomName(row.systemRoom) : '',
        表格业主: row.ownerName || '',
        房屋业主: row.systemRoom ? row.systemRoom.ownerName || '' : '',
        表格手机号: row.ownerTel || '',
        房屋业主手机号: row.systemRoom ? row.systemRoom.link || '' : '',
        源欠费总额: this.formatMoney(row.sourceTotal),
        欠费期间: this.formatMonthRange(row.months),
        源表面积: row.sourceArea > 0 ? row.sourceArea : '',
        匹配方式: row.matchMode || '',
        处理说明: row.ignoreReason || row.matchReason || '',
        备注: row.remark || ''
      }))
      const worksheet = XLSX.utils.json_to_sheet(data)
      worksheet['!cols'] = [
        { wch: 8 }, { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 14 },
        { wch: 20 }, { wch: 20 }, { wch: 14 }, { wch: 25 }, { wch: 12 },
        { wch: 18 }, { wch: 32 }, { wch: 24 }
      ]
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, '欠费信息')
      const safeFileName = String(fileName || '欠费信息').replace(/[\\/:*?"<>|]/g, '_')
      XLSX.writeFile(workbook, `${safeFileName}.xlsx`)
    },
    openManualRoom(row, fromMatched) {
      this.manualRoomRow = row
      this.manualRoomFromMatched = fromMatched
      this.manualRoomKeyword = row.sourceRoomText === '-' ? '' : row.sourceRoomText
      this.manualRoomVisible = true
    },
    applyManualRoom(room) {
      if (!this.manualRoomRow) return
      const source = { ...this.manualRoomRow }
      delete source.systemRoom
      delete source.warnings
      delete source.calculation
      const matched = this.createMatchedRow(source, room, '手工匹配')
      if (this.manualRoomFromMatched) {
        const index = this.matchedRows.findIndex(item => item.id === this.manualRoomRow.id)
        if (index >= 0) this.$set(this.matchedRows, index, matched)
      } else {
        this.unmatchedRows = this.unmatchedRows.filter(item => item.id !== this.manualRoomRow.id)
        this.matchedRows.push(matched)
      }
      this.refreshDuplicateRoomWarnings()
      this.manualRoomVisible = false
      this.manualRoomRow = null
      this.matchTab = 'matched'
    },
    previousStep() {
      if (this.activeStep === 4) this.activeStep = 3
      else if (this.activeStep === 3) this.activeStep = 2
      else if (this.activeStep === 2) this.activeStep = 1
      else if (this.activeStep === 1) this.activeStep = 0
    },
    goToRules() {
      this.ensureDefaultRuleCoverage()
      this.activeStep = 3
    },
    clearCalculationPreview() {
      this.calculatedRows = []
      this.detailVisible = false
      this.detailRow = null
      this.resetDetailEditor()
      this.refreshSelectionSummary()
    },
    handleAmountModeChange() {
      this.clearCalculationPreview()
      if (this.isDirectTotalMode && !this.totalFeeConfigId && this.directTotalFeeConfigs.length === 1) {
        this.totalFeeConfigId = this.directTotalFeeConfigs[0].configId
      }
    },
    ensureDefaultRuleCoverage() {
      const months = this.matchedRows.reduce((result, row) => result.concat(row.months || []), []).sort()
      if (!months.length) return
      const defaultConfig = this.feeRuleConfigs.find(item => isPropertyFeeConfig(item) && /物业|物管/.test(item.feeName || ''))
        || this.feeRuleConfigs.find(isPropertyFeeConfig)
        || this.feeRuleConfigs.find(item => /物业|物管/.test(item.feeName || ''))
        || this.feeRuleConfigs[0]
      if (!this.propertyRules.length) this.propertyRules = [this.createPropertyRule()]
      const first = this.propertyRules[0]
      if (!first.startMonth) first.startMonth = months[0]
      if (!first.endMonth) first.endMonth = months[months.length - 1]
      if (!first.configId && defaultConfig) first.configId = defaultConfig.configId
    },
    createPropertyRule() {
      return { id: this.createId('property'), startMonth: '', endMonth: '', rate: 0, configId: '' }
    },
    createFixedFeeRule() {
      const months = this.matchedRows.reduce((result, row) => result.concat(row.months || []), []).sort()
      return {
        id: this.createId('fixed'),
        name: '',
        startMonth: months[0] || '',
        endMonth: months[months.length - 1] || '',
        amount: 0,
        configId: ''
      }
    },
    addPropertyRule() {
      this.propertyRules.push(this.createPropertyRule())
    },
    removePropertyRule(index) {
      if (this.propertyRules.length > 1) this.propertyRules.splice(index, 1)
    },
    addFixedFeeRule() {
      this.fixedFeeRules.push(this.createFixedFeeRule())
    },
    removeFixedFeeRule(index) {
      this.fixedFeeRules.splice(index, 1)
    },
    calculatePreview() {
      if (this.ruleErrors.length) return
      this.calculatedRows = this.matchedRows.map(row => this.calculateRow(row))
      this.previewTab = 'calculated'
      this.activeStep = 4
      this.refreshSelectionSummary()
    },
    calculateRow(row) {
      const result = { ...row, calculation: null, selected: false }
      if (row.duplicateRoom) {
        result.calcStatus = 'invalid'
        result.calcMessage = '同一房屋存在多条源汇总记录，请先合并后重新上传'
        return result
      }
      if (!row.systemRoom.ownerName) {
        result.calcStatus = 'invalid'
        result.calcMessage = '系统房屋未绑定业主，无法通过现有导入接口校验'
        return result
      }
      if (this.isDirectTotalMode) return this.prepareDynamicTotalRow(result)
      if (row.sourceAreaError) {
        result.calcStatus = 'invalid'
        result.calcMessage = row.sourceAreaError
        return result
      }
      const systemArea = Number(row.systemRoom.builtUpArea || 0)
      const area = this.areaBasis === 'source-first' && row.sourceArea > 0 ? row.sourceArea : systemArea
      if (!(area > 0)) {
        result.calcStatus = 'invalid'
        result.calcMessage = '计费面积缺失或不大于0'
        return result
      }
      const monthDetails = []
      for (const month of row.months) {
        const matchedRules = this.propertyRules.filter(rule => this.monthInRange(month, rule))
        if (matchedRules.length !== 1) {
          result.calcStatus = 'invalid'
          result.calcMessage = matchedRules.length ? `${month}命中多条物业费规则` : `${month}未配置物业费规则`
          return result
        }
        const propertyRule = matchedRules[0]
        const propertyConfig = this.findFeeConfig(propertyRule.configId)
        const propertyAmount = this.roundBillingAmount(area * Number(propertyRule.rate))
        const items = [{
          type: 'property',
          name: '物业费',
          configId: propertyRule.configId,
          configName: propertyConfig ? propertyConfig.feeName : '',
          rate: Number(propertyRule.rate),
          area: this.roundNumber(area, 4),
          amount: propertyAmount,
          baseAmount: propertyAmount,
          autoAmount: propertyAmount,
          balanceOffsetAmount: 0,
          manuallyEdited: false
        }]
        this.fixedFeeRules.filter(rule => this.monthInRange(month, rule)).forEach(rule => {
          const config = this.findFeeConfig(rule.configId)
          const fixedAmount = this.roundBillingAmount(Number(rule.amount))
          items.push({
            type: 'fixed',
            name: rule.name,
            configId: rule.configId,
            configName: config ? config.feeName : '',
            amount: fixedAmount,
            baseAmount: fixedAmount,
            autoAmount: fixedAmount,
            balanceOffsetAmount: 0,
            manuallyEdited: false
          })
        })
        monthDetails.push({
          month,
          items,
          total: this.roundBillingAmount(items.reduce((sum, item) => sum + item.amount, 0))
        })
      }
      const ruleTotal = this.roundBillingAmount(monthDetails.reduce((sum, month) => sum + month.total, 0))
      const balanceOffset = {
        enabled: false,
        requested: 0,
        applied: 0,
        remaining: 0
      }
      let calculatedTotal = this.roundBillingAmount(monthDetails.reduce((sum, month) => sum + month.total, 0))
      let difference = this.roundMoney(Number(row.sourceTotal) - calculatedTotal)
      if (Math.abs(difference) <= 0.01 && monthDetails.length) {
        const oldestProperty = monthDetails[0].items.find(item => item.type === 'property')
        if (oldestProperty && oldestProperty.amount + difference > 0) {
          oldestProperty.amount = this.roundBillingAmount(oldestProperty.amount + difference)
          monthDetails[0].total = this.roundBillingAmount(monthDetails[0].items.reduce((sum, item) => sum + item.amount, 0))
          calculatedTotal = this.roundBillingAmount(monthDetails.reduce((sum, month) => sum + month.total, 0))
          difference = this.roundMoney(Number(row.sourceTotal) - calculatedTotal)
        }
      }
      monthDetails.forEach(month => {
        month.items.forEach(item => {
          item.autoAmount = item.amount
        })
      })
      result.calculation = {
        area: this.roundNumber(area, 4),
        areaSource: this.areaBasis === 'source-first' && row.sourceArea > 0 ? '源表面积' : '系统建筑面积',
        roundingConfig: { ...this.roundingConfig },
        ruleTotal,
        balanceOffset,
        months: monthDetails,
        total: calculatedTotal,
        difference
      }
      this.updateCalculatedRowStatus(result, true)
      return result
    },
    prepareDynamicTotalRow(result) {
      const config = this.directTotalFeeConfigs.find(item => item.configId === this.totalFeeConfigId)
      const amount = Number(result.sourceTotal)
      if (!config || !Number.isFinite(amount) || amount <= 0 || !result.months || !result.months.length) {
        result.calcStatus = 'invalid'
        result.calcMessage = !config ? '请选择有效的动态费用收费项' : '欠费合计或欠费期间无效'
        return result
      }
      const firstMonth = result.months[0]
      const lastMonth = result.months[result.months.length - 1]
      result.calculation = {
        mode: 'dynamic-total',
        total: amount,
        difference: 0,
        months: [{
          month: firstMonth,
          startTime: `${firstMonth}-01`,
          endTime: this.monthEndDate(lastMonth),
          total: amount,
          items: [{ type: 'dynamic-total', name: config.feeName, configId: config.configId, configName: config.feeName, amount }]
        }]
      }
      result.calcStatus = 'pass'
      result.calcMessage = '按源表合计金额直接导入'
      result.selected = true
      return result
    },
    canApplyBalanceOffset(row) {
      if (!row || !row.calculation || row.calculation.mode === 'dynamic-total') return false
      const difference = Number(row.calculation.difference)
      return Number.isFinite(difference) && difference < -0.001
    },
    applyCalculatedBalanceOffset(row) {
      if (!this.canApplyBalanceOffset(row)) {
        this.$message.warning('当前没有可冲正的计算差额')
        return
      }
      const difference = this.roundMoney(Math.abs(Number(row.calculation.difference)))
      if (!(difference > 0)) return
      const balanceOffset = row.calculation.balanceOffset || {
        enabled: false,
        requested: 0,
        applied: 0,
        remaining: 0
      }
      const offsetResult = this.applyBalanceOffset(row.calculation.months, difference)
      balanceOffset.enabled = true
      balanceOffset.requested = this.roundMoney(Number(balanceOffset.requested || 0) + offsetResult.requested)
      balanceOffset.applied = this.roundMoney(Number(balanceOffset.applied || 0) + offsetResult.applied)
      this.$set(row.calculation, 'balanceOffset', balanceOffset)
      this.$set(row, 'balanceOffsetEnabled', true)
      this.updateCalculatedRowStatus(row, false)
      balanceOffset.remaining = row.calculation.difference < -0.001
        ? this.roundMoney(Math.abs(row.calculation.difference))
        : 0
      this.refreshSelectionSummary()
      if (offsetResult.applied > 0 && offsetResult.remaining <= 0.001) {
        this.$message.success(`已按最早月份依次冲正${this.formatMoney(offsetResult.applied)}元`)
      } else if (offsetResult.applied > 0) {
        this.$message.warning(`已冲正${this.formatMoney(offsetResult.applied)}元，仍有${this.formatMoney(balanceOffset.remaining)}元差额未冲抵`)
      } else {
        this.$message.warning('当前欠费明细金额不足，无法继续冲正')
      }
    },
    applyBalanceOffset(monthDetails, amount) {
      const requested = this.roundMoney(Math.max(0, Number(amount) || 0))
      let remaining = requested
      let applied = 0
      for (const month of monthDetails) {
        for (const item of month.items) {
          if (remaining <= 0.001) break
          const currentAmount = Number(item.amount || 0)
          if (!(currentAmount > 0)) continue
          const expectedReduction = Math.min(currentAmount, remaining)
          const nextAmount = Math.max(0, this.roundBillingAmount(currentAmount - expectedReduction))
          const actualReduction = this.roundMoney(currentAmount - nextAmount)
          if (!(actualReduction > 0) || actualReduction - remaining > 0.001) continue
          item.amount = nextAmount
          item.balanceOffsetAmount = this.roundMoney(Number(item.balanceOffsetAmount || 0) + actualReduction)
          applied = this.roundMoney(applied + actualReduction)
          remaining = this.roundMoney(requested - applied)
        }
        month.total = this.roundBillingAmount(month.items.reduce((sum, item) => sum + Number(item.amount || 0), 0))
        if (remaining <= 0.001) break
      }
      return {
        requested,
        applied,
        remaining: Math.max(0, remaining)
      }
    },
    updateCalculatedRowStatus(row, initialCalculation = false) {
      if (!row || !row.calculation) return
      const previousStatus = row.calcStatus
      const wasSelected = Boolean(row.selected)
      const months = row.calculation.months || []
      months.forEach(month => {
        month.total = this.roundBillingAmount(month.items.reduce((sum, item) => sum + Number(item.amount || 0), 0))
      })
      row.calculation.total = this.roundBillingAmount(months.reduce((sum, month) => sum + Number(month.total || 0), 0))
      row.calculation.difference = this.roundMoney(Number(row.sourceTotal) - row.calculation.total)
      const manualEditedCount = months.reduce((count, month) => {
        return count + month.items.filter(item => item.manuallyEdited).length
      }, 0)
      row.calculation.manualEditedCount = manualEditedCount
      if (Math.abs(row.calculation.difference) <= 0.01) {
        const messages = []
        const balanceOffset = row.calculation.balanceOffset
        if (balanceOffset && balanceOffset.applied > 0) {
          messages.push(`已按最早月份依次冲正${this.formatMoney(balanceOffset.applied)}元`)
        }
        if (manualEditedCount) messages.push(`已手工编辑${manualEditedCount}条欠费金额`)
        row.calcStatus = 'pass'
        row.calcMessage = messages.length ? messages.join('；') : '源总额与月度试算一致'
        row.selected = initialCalculation || previousStatus !== 'pass' ? true : wasSelected
      } else {
        const differenceText = this.formatMoney(Math.abs(row.calculation.difference))
        const balanceOffset = row.calculation.balanceOffset
        row.calcStatus = 'mismatch'
        row.selected = false
        if (manualEditedCount) {
          row.calcMessage = `手工编辑后与源总额相差${differenceText}元`
        } else if (balanceOffset && balanceOffset.enabled && balanceOffset.requested > 0) {
          row.calcMessage = `余额抵扣差额冲正后仍相差${differenceText}元`
        } else if (balanceOffset && balanceOffset.enabled && row.calculation.difference > 0) {
          row.calcMessage = `源总额高于规则试算，无法通过余额抵扣冲正，相差${differenceText}元`
        } else {
          row.calcMessage = `源总额与试算合计相差${differenceText}元`
        }
      }
    },
    refreshSelectionSummary() {
      this.selectionVersion += 1
    },
    openCalculationDetail(row) {
      this.resetDetailEditor()
      this.detailRow = row
      this.detailVisible = true
    },
    startEditDetail(detail) {
      if (detail.item.type === 'dynamic-total') return
      this.editingDetailKey = detail.key
      this.editingDetailAmount = Number(detail.item.amount || 0)
    },
    resetDetailEditor() {
      this.editingDetailKey = ''
      this.editingDetailAmount = 0
    },
    saveDetailAmount(detail) {
      if (detail.item.type === 'dynamic-total') return
      const number = Number(this.editingDetailAmount)
      if (!Number.isFinite(number) || number < 0) {
        this.$message.warning('欠费金额必须为大于或等于0的有效数字')
        return
      }
      const amount = this.roundBillingAmount(number)
      const autoAmount = Number(detail.item.autoAmount === undefined ? detail.item.amount : detail.item.autoAmount)
      this.$set(detail.item, 'amount', amount)
      this.$set(detail.item, 'manuallyEdited', Math.abs(amount - autoAmount) > 0.000001)
      this.updateCalculatedRowStatus(this.detailRow, false)
      this.refreshSelectionSummary()
      this.resetDetailEditor()
      this.$message.success('欠费金额已更新')
    },
    async submitImport() {
      if (!this.canSubmit) return
      if (!this.isValidImportTarget(this.importTargetType, this.selectedRoomType)) {
        this.$message.error('导入类型无效，请返回上传步骤重新选择')
        return
      }
      const rows = []
      this.selectedRows.forEach(sourceRow => {
        sourceRow.calculation.months.forEach(month => {
          const startTime = month.startTime || `${month.month}-01`
          const endTime = month.endTime || this.monthEndDate(month.month)
          month.items.filter(item => Number(item.amount) > 0).forEach(item => {
            rows.push({
              importTargetType: this.importTargetType,
              roomType: this.selectedRoomType,
              roomName: this.getSystemRoomName(sourceRow.systemRoom),
              ownerName: sourceRow.systemRoom.ownerName || '',
              ownerTel: sourceRow.systemRoom.link || '',
              feeConfigName: item.configName,
              configId: item.configId,
              oweAmount: item.type === 'dynamic-total' ? this.formatMoney(item.amount) : this.formatBillingMoney(item.amount),
              startTime,
              endTime,
              remark: this.buildImportRemark(sourceRow, month.month, item)
            })
          })
        })
      })
      this.submitting = true
      try {
        const response = await this.submitHandler({
          communityId: this.communityId,
          importTargetType: this.importTargetType,
          roomType: this.selectedRoomType,
          sourceFileName: this.sourceFileName,
          sourceCount: this.sourceRows.length,
          matchedCount: this.matchedRows.length,
          unmatchedCount: this.unmatchedRows.length,
          ignoredCount: this.ignoredRows.length,
          roundingConfig: { ...this.roundingConfig },
          selectedCount: this.selectedCalculationCount,
          rows
        })
        if (this.draftSavedAt) {
          const draftCleared = await this.clearDraft(false)
          if (!draftCleared) this.$message.warning('导入成功，但本地草稿清除失败；下次打开时请放弃旧草稿')
        }
        this.$emit('success', response, { rows, selectedRows: this.selectedRows })
        this.dialogVisible = false
      } catch (error) {
        const message = error && (error.message || error.msg)
        if (!(error && Array.isArray(error.data) && error.data.length)) {
          this.$message.error(message || '历史欠费导入失败')
        }
        this.$emit('error', error)
      } finally {
        this.submitting = false
      }
    },
    buildImportRemark(sourceRow, month, item) {
      const parts = [
        '历史欠费兼容导入',
        `导入类型:${this.importTargetLabel}`,
        `源文件:${this.sourceFileName}`,
        `源行:${sourceRow.sourceRowNumber}`,
        item.type === 'dynamic-total' ? `欠费期间:${sourceRow.months[0]}至${sourceRow.months[sourceRow.months.length - 1]}` : `计费月:${month}`,
        `费用:${item.name}`,
        item.type === 'dynamic-total' ? '动态费用按源合计导入' : `金额取整:${this.roundingDescription}`
      ]
      if (Number(item.balanceOffsetAmount) > 0) parts.push(`余额冲正:${this.formatBillingMoney(item.balanceOffsetAmount)}`)
      if (item.manuallyEdited) parts.push('金额已手工编辑')
      if (sourceRow.remark) parts.push(`源备注:${sourceRow.remark}`)
      return parts.join('|').slice(0, 200)
    },
    resolvePeriod(values) {
      const startMonth = this.normalizeMonthValue(values.periodStart)
      const endMonth = this.normalizeMonthValue(values.periodEnd)
      const latestMonth = this.normalizeMonthValue(values.latestOweMonth)
      const oweMonths = this.parsePositiveInteger(values.oweMonths)
      const hasDateValue = Boolean(values.periodStart || values.periodEnd)
      const hasLatestValue = Boolean(values.latestOweMonth || values.oweMonths)
      let dateMonths = null
      let latestMonths = null
      if (hasDateValue) {
        if (!startMonth || !endMonth) return { error: '计费开始、结束时间须同时填写且格式有效' }
        dateMonths = this.monthRange(startMonth, endMonth)
        if (!dateMonths.length) return { error: '计费开始时间不能晚于结束时间，且最长支持240个月' }
      }
      if (hasLatestValue) {
        if (!latestMonth || !oweMonths) return { error: '最晚欠费月份、欠费月数须同时填写且格式有效' }
        latestMonths = this.reverseMonthRange(latestMonth, oweMonths)
        if (!latestMonths.length) return { error: '欠费月数必须在1至240之间' }
      }
      if (!dateMonths && !latestMonths) return { error: '缺少有效欠费周期' }
      if (dateMonths && latestMonths) {
        const same = dateMonths.length === latestMonths.length && dateMonths.every((month, index) => month === latestMonths[index])
        if (!same) return { error: '计费时间范围与最晚欠费月份+欠费月数不一致' }
      }
      return { months: dateMonths || latestMonths }
    },
    normalizeMonthValue(value) {
      if (EMPTY_VALUE(value)) return ''
      if (value instanceof Date && !Number.isNaN(value.getTime())) return `${value.getFullYear()}-${this.pad(value.getMonth() + 1)}`
      const text = String(value).trim()
      if (/^\d{5}(?:\.\d+)?$/.test(text)) {
        const parsed = XLSX.SSF.parse_date_code(Number(text))
        if (parsed && parsed.y && parsed.m) return `${parsed.y}-${this.pad(parsed.m)}`
      }
      const normalized = text.replace(/[年/.]/g, '-').replace(/月.*$/, '').replace(/日/g, '')
      let match = normalized.match(/^(\d{4})-?(\d{1,2})/)
      if (!match && /^\d{6}$/.test(text)) match = [text, text.slice(0, 4), text.slice(4, 6)]
      if (!match) return ''
      const year = Number(match[1])
      const month = Number(match[2])
      if (year < 1900 || year > 2200 || month < 1 || month > 12) return ''
      return `${year}-${this.pad(month)}`
    },
    monthRange(startMonth, endMonth) {
      if (!startMonth || !endMonth || startMonth > endMonth) return []
      const result = []
      let current = startMonth
      while (current <= endMonth && result.length < 240) {
        result.push(current)
        current = this.shiftMonth(current, 1)
      }
      if (current <= endMonth) return []
      return result
    },
    reverseMonthRange(latestMonth, count) {
      if (!latestMonth || !(count >= 1 && count <= 240)) return []
      const result = []
      for (let offset = count - 1; offset >= 0; offset -= 1) result.push(this.shiftMonth(latestMonth, -offset))
      return result
    },
    shiftMonth(month, offset) {
      const parts = String(month).split('-').map(Number)
      const date = new Date(parts[0], parts[1] - 1 + offset, 1)
      return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}`
    },
    monthEndDate(month) {
      const parts = month.split('-').map(Number)
      const day = new Date(parts[0], parts[1], 0).getDate()
      return `${month}-${this.pad(day)}`
    },
    parseMoney(value) {
      if (EMPTY_VALUE(value)) return 0
      let text = String(value).trim().replace(/[￥¥$€,，\s]/g, '').replace(/元/g, '')
      if (/^\(.*\)$/.test(text)) text = `-${text.slice(1, -1)}`
      const number = Number(text)
      return Number.isFinite(number) ? this.roundMoney(number) : 0
    },
    parsePositiveInteger(value) {
      const number = Number(String(value || '').replace(/个月|月/g, '').trim())
      return Number.isInteger(number) && number > 0 ? number : 0
    },
    normalizeRoomLocator(value) {
      return String(value || '').trim().toUpperCase()
        .replace(/[—–－]/g, '-')
        .replace(/第/g, '')
        .replace(/(?:号楼|号栋|号幢|楼栋)/g, '-')
        .replace(/[幢栋座楼#]/g, '-')
        .replace(/单元/g, '-')
        .replace(/[室号]$/g, '')
        .replace(/[\s_]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
    },
    normalizeRoomNum(value) {
      return this.normalizeRoomLocator(value).replace(/[室号]$/g, '')
    },
    sameRoomPart(source, system) {
      return this.normalizeRoomLocator(source) === this.normalizeRoomLocator(system)
    },
    getRoomPartValue(room, part) {
      if (!room) return ''
      const directValue = this.normalizeSourceValue(room[part])
      if (directValue) return directValue
      if (part !== 'floorNum') return ''

      // 部分房屋查询接口缺少 floorNum，但 roomName 仍保留“5栋-...”前缀。
      const roomName = this.normalizeSourceValue(room.roomName)
      const match = roomName.match(/^(.+?)(?:号楼|号栋|号幢|栋|幢|座|楼)/i)
      return match ? match[1] : ''
    },
    roomPartMatches(source, room, part) {
      if (!source) return true
      const systemValue = part ? this.getRoomPartValue(room, part) : room
      return Boolean(systemValue) && this.sameRoomPart(source, systemValue)
    },
    normalizeSearchText(value) {
      return String(value || '').trim().toLowerCase().replace(/\s+/g, '')
    },
    isValidImportTarget(importTargetType, roomType) {
      return IMPORT_TARGET_OPTIONS.some(option => {
        return option.value === String(importTargetType || '') && option.roomType === String(roomType || '')
      })
    },
    normalizePhone(value) {
      return String(value || '').replace(/[^0-9]/g, '')
    },
    getSystemRoomName(room) {
      if (!room) return '-'
      return room.roomName || [room.floorNum, room.unitNum, room.roomNum].filter(Boolean).join('-') || room.roomId || '-'
    },
    formatMonthRange(months) {
      if (!months || !months.length) return '-'
      return months.length === 1 ? months[0] : `${months[0]} 至 ${months[months.length - 1]}（${months.length}个月）`
    },
    monthInRange(month, rule) {
      return Boolean(rule && rule.startMonth && rule.endMonth && month >= rule.startMonth && month <= rule.endMonth)
    },
    rangesOverlap(first, second) {
      if (!first.startMonth || !first.endMonth || !second.startMonth || !second.endMonth) return false
      return first.startMonth <= second.endMonth && second.startMonth <= first.endMonth
    },
    findFeeConfig(configId) {
      return this.feeRuleConfigs.find(item => item.configId === configId)
    },
    feeConfigLabel(config) {
      return `${config.feeName}（${config.configId}）`
    },
    formatMoney(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(2) : '0.00'
    },
    formatBillingMoney(value) {
      if (this.isDirectTotalMode) return this.formatMoney(value)
      const decimalPlace = Number(this.roundingConfig && this.roundingConfig.decimalPlace)
      const places = Number.isInteger(decimalPlace) && decimalPlace >= 0 && decimalPlace <= 4 ? decimalPlace : 2
      return this.roundBillingAmount(value).toFixed(places)
    },
    roundBillingAmount(value) {
      const decimalPlace = Number(this.roundingConfig && this.roundingConfig.decimalPlace)
      const scale = String(this.roundingConfig && this.roundingConfig.scale)
      const validDecimalPlace = Number.isInteger(decimalPlace) && decimalPlace >= 0 && decimalPlace <= 4
        ? decimalPlace
        : 2
      const validScale = ROUNDING_SCALE_OPTIONS.some(option => option.value === scale) ? scale : '1'
      return this.roundMoneyByScale(value, validDecimalPlace, validScale)
    },
    roundMoneyByScale(value, decimalPlace, scale) {
      const number = Number(value)
      if (!Number.isFinite(number)) return 0
      const factor = Math.pow(10, decimalPlace)
      const scaled = number * factor
      const epsilon = Number.EPSILON * Math.max(1, Math.abs(scaled)) * 10
      let rounded
      if (scale === '3') {
        rounded = scaled >= 0 ? Math.ceil(scaled - epsilon) : Math.floor(scaled + epsilon)
      } else if (scale === '4') {
        rounded = scaled >= 0 ? Math.floor(scaled + epsilon) : Math.ceil(scaled - epsilon)
      } else {
        rounded = scaled >= 0 ? Math.floor(scaled + 0.5 + epsilon) : Math.ceil(scaled - 0.5 - epsilon)
      }
      return rounded / factor
    },
    roundMoney(value) {
      return Math.round((Number(value) + Number.EPSILON) * 100) / 100
    },
    roundNumber(value, precision) {
      const factor = Math.pow(10, precision)
      return Math.round((Number(value) + Number.EPSILON) * factor) / factor
    },
    pad(value) {
      return String(value).padStart(2, '0')
    },
    createId(prefix) {
      return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`
    }
  }
}
</script>

<style lang="scss" scoped>
.history-owe-fee-import {
  .dialog-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 10px;
  }

  .dialog-footer .el-button + .el-button {
    margin-left: 0;
  }

  .draft-status {
    margin-right: auto;
    color: #909399;
    font-size: 12px;
  }

  .import-steps {
    margin-bottom: 18px;
  }

  .import-target-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 36px;
    margin-bottom: 14px;
    padding: 0 12px;
    border-left: 3px solid #409eff;
    background: #f4f8ff;
    color: #606266;
    font-size: 13px;
  }

  .step-section {
    min-height: 560px;
  }

  .upload-section {
    min-height: 500px;
    padding-top: 20px;
    text-align: center;
  }

  .import-target-selector {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 14px;
    margin: 0 auto 24px;

    > span {
      flex-basis: 100%;
      color: #909399;
      font-size: 12px;
    }
  }

  .target-required-tip {
    margin-top: 8px;
    color: #e6a23c;
    font-size: 13px;
  }

  .upload-notes {
    display: flex;
    justify-content: center;
    gap: 28px;
    margin-top: 24px;
    color: #606266;

    i {
      margin-right: 5px;
      color: #409eff;
    }
  }

  .parsing-tip {
    margin-top: 20px;
    color: #409eff;
  }

  .source-overview,
  .rule-toolbar,
  .rule-title,
  .match-summary,
  .preview-summary,
  .detail-overview {
    display: flex;
    align-items: center;
  }

  .source-overview,
  .rule-toolbar,
  .rule-title {
    justify-content: space-between;
    margin-bottom: 14px;
  }

  .source-overview strong,
  .source-overview span {
    display: block;
  }

  .source-overview span {
    margin-top: 4px;
    color: #909399;
  }

  .source-actions {
    display: flex;
    gap: 10px;
  }

  .step-alert {
    margin-bottom: 14px;
  }

  .field-name {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .field-description {
    margin-top: 4px;
    color: #909399;
    font-size: 12px;
    line-height: 18px;
  }

  .sample-values {
    color: #606266;
  }

  .value-mapping-control {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }

  .mapping-warning {
    display: block;
    margin-top: 4px;
    font-size: 12px;
  }

  .value-mapping-heading p,
  .value-mapping-note {
    margin: 0 0 10px;
    color: #606266;
    line-height: 20px;
  }

  .value-mapping-note {
    margin-top: 12px;
    color: #909399;
    font-size: 12px;
  }

  .empty-text {
    color: #c0c4cc;
  }

  .match-summary {
    gap: 32px;
    min-height: 48px;
    margin-bottom: 4px;
    color: #606266;
    flex-wrap: wrap;

    strong {
      margin-left: 5px;
      font-size: 20px;
    }
  }

  .match-actions {
    display: flex;
    gap: 8px;
    margin-left: auto;
  }

  .rule-toolbar > div {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .rounding-config {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 14px;
    padding: 12px 14px;
    border: 1px solid #ebeef5;
    background: #f8fafc;

    strong,
    > div:first-child > span {
      display: block;
    }

    > div:first-child > span {
      margin-top: 4px;
      color: #909399;
      font-size: 12px;
    }
  }

  .rounding-controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    color: #606266;
    white-space: nowrap;
  }

  .rule-range {
    color: #606266;
  }

  .rule-title {
    margin-top: 18px;

    strong,
    span {
      display: block;
    }

    span {
      margin-top: 4px;
      color: #909399;
      font-size: 12px;
    }
  }

  .fixed-title {
    margin-top: 28px;
  }

  .preview-summary {
    gap: 0;
    min-height: 72px;
    margin-bottom: 12px;
    border: 1px solid #ebeef5;

    > div {
      flex: 1;
      min-width: 110px;
      text-align: center;
      border-right: 1px solid #ebeef5;

      &:last-child {
        border-right: 0;
      }
    }

    strong,
    span {
      display: block;
    }

    strong {
      font-size: 22px;
    }

    span {
      margin-top: 4px;
      color: #909399;
      font-size: 12px;
    }
  }

  .preview-rounding {
    margin: -4px 0 12px;
    color: #606266;
    font-size: 12px;
  }

  .difference-display {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 4px;
  }

  .status-message {
    margin-left: 8px;
    color: #606266;
  }

  .phone-match-tag {
    margin-left: 6px;
  }

  .success-text {
    color: #67c23a;
  }

  .warning-text {
    color: #e6a23c;
  }

  .danger-text {
    color: #f56c6c;
  }

  .manual-search {
    margin-bottom: 14px;
  }

  .candidate-tip {
    margin-top: 10px;
    color: #909399;
    font-size: 12px;
  }

  .owner-type-tag {
    margin-left: 6px;
  }

}

.history-owe-fee-import ::v-deep .el-upload-dragger {
  width: 560px;
  max-width: calc(100vw - 120px);
  height: 220px;
  padding-top: 24px;
}

.history-owe-fee-import ::v-deep .el-upload-dragger .el-icon-upload {
  margin-top: 34px;
}

.history-owe-fee-import ::v-deep .mapping-table .cell,
.history-owe-fee-import ::v-deep .rule-table .cell {
  line-height: 20px;
}

.history-owe-fee-import ::v-deep .history-owe-fee-dialog .el-dialog__body {
  max-height: calc(96vh - 170px);
  overflow-y: auto;
}
</style>

<style lang="scss">
.history-owe-fee-detail-dialog {
  max-width: calc(100vw - 32px);
  box-sizing: border-box;

  .el-dialog__body {
    overflow-x: hidden;
  }

  .el-table {
    width: 100%;
  }

  .detail-overview {
    display: flex;
    align-items: center;
    gap: 30px;
    margin-bottom: 14px;
    color: #606266;
    flex-wrap: wrap;
  }

  .detail-amount-input {
    width: 150px;
  }

  .detail-amount-display {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 6px;
  }

  .balance-adjusted-amount {
    color: #e6a23c;
    font-weight: 600;
  }

  .manual-edited-amount {
    color: #409eff;
    font-weight: 600;
  }
}
</style>
